import { useState, useEffect, useRef, Component, type ReactNode, type ErrorInfo } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { Link } from "wouter";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { NumberField } from "@/components/ui/number-field";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Switch } from "@/components/ui/switch";

// Error boundary to prevent blank pages on unexpected errors
class AdminPanelErrorBoundary extends Component<
  { children: ReactNode },
  { hasError: boolean; error: Error | null }
> {
  constructor(props: { children: ReactNode }) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error) {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error("AdminPanel Error:", error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      const errorMessage = this.state.error?.message || "Unknown error";
      const errorStack = this.state.error?.stack || "";
      return (
        <div className="p-6 space-y-6">
          <div className="flex flex-col items-center justify-center min-h-[400px] text-center">
            <div className="w-16 h-16 text-destructive mb-4 flex items-center justify-center">
              <svg xmlns="http://www.w3.org/2000/svg" width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10"/>
                <line x1="15" y1="9" x2="9" y2="15"/>
                <line x1="9" y1="9" x2="15" y2="15"/>
              </svg>
            </div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-2">
              Something went wrong
            </h1>
            <p className="text-gray-600 dark:text-gray-400 mb-4 max-w-md">
              The admin panel encountered an error. Please try refreshing the page.
            </p>
            <details className="text-left bg-gray-100 dark:bg-gray-800 p-4 rounded-lg mb-4 max-w-lg overflow-auto">
              <summary className="cursor-pointer text-sm font-medium">Error Details (click to expand)</summary>
              <p className="text-sm text-red-600 dark:text-red-400 mt-2 font-mono">{errorMessage}</p>
              <pre className="text-xs text-gray-500 mt-2 whitespace-pre-wrap">{errorStack.split('\n').slice(0, 5).join('\n')}</pre>
            </details>
            <Button onClick={() => window.location.reload()}>
              Refresh Page
            </Button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Plus, Edit, Trash2, GripVertical, Save, X, Users, Shield, Store, Layout, Megaphone, Building, Lightbulb, RefreshCw, Sparkles, Download, Upload, Database, Cloud, Calendar, Palette, Type, RotateCcw, Smile, FileJson, Search, Bell, Stethoscope, CheckCircle2, XCircle, ArrowLeft, Menu, ClipboardList, Settings2, ChevronDown, ChevronRight, DollarSign, Timer, Lock, BookUser, AlertTriangle } from "lucide-react";
import { menuItemDefinitions } from "@/lib/menu-items";
import { formatFeatureName } from "@workspace/api-client-react";
import { format } from "date-fns";
import { useAuth } from "@/hooks/useAuth";
import { useToast } from "@/hooks/use-toast";
import { apiRequest, queryClient, getQueryFn } from "@/lib/queryClient";
import { useNavigationOrder } from "@/hooks/useNavigationOrder";
import UpdatesEditor from "@/components/updates-editor";
import type { Category, NewCategory, User, Tenant, Update } from "@shared/schema";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import {
  useSortable,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

interface SortableCategoryItemProps {
  category: Category;
  onEdit: (category: Category) => void;
  onDelete: (id: string) => void;
}

// Read-only row for goals-scoped categories (no drag handle, no edit/delete)
function GoalCategoryItem({ category }: { category: Category }) {
  return (
    <div className="flex items-center gap-3 p-3 bg-blue-50/50 dark:bg-blue-900/10 border border-blue-100 dark:border-blue-900/30 rounded-lg">
      <div className="w-4 h-4 rounded-full flex-shrink-0" style={{ backgroundColor: category.color }} />
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="font-medium text-gray-900 dark:text-gray-100">{category.name}</span>
          <Badge className="text-xs bg-blue-100 text-blue-700 border-blue-200 dark:bg-blue-900/40 dark:text-blue-300 dark:border-blue-700 hover:bg-blue-100">
            Goals
          </Badge>
          {category.isDefault && (
            <Badge variant="secondary" className="text-xs">Default</Badge>
          )}
        </div>
        {category.description && (
          <p className="text-sm text-gray-500 dark:text-gray-400 truncate">{category.description}</p>
        )}
      </div>
      <Lock className="w-4 h-4 text-gray-400 flex-shrink-0" aria-label="Read-only" />
    </div>
  );
}

function SortableCategoryItem({ category, onEdit, onDelete }: SortableCategoryItemProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: category.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className="flex items-center gap-3 p-3 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg"
    >
      <div
        {...attributes}
        {...listeners}
        className="cursor-grab hover:cursor-grabbing text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300"
      >
        <GripVertical className="w-4 h-4" />
      </div>
      
      <div
        className="w-4 h-4 rounded-full flex-shrink-0"
        style={{ backgroundColor: category.color }}
      />
      
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="font-medium text-gray-900 dark:text-gray-100">
            {category.name}
          </span>
          {category.isDefault && (
            <Badge variant="secondary" className="text-xs">
              Default
            </Badge>
          )}
          {category.scope === "goals" && (
            <Badge variant="outline" className="text-xs text-blue-600 border-blue-300">
              Goals only
            </Badge>
          )}
          {category.scope === "sops" && (
            <Badge variant="outline" className="text-xs text-purple-600 border-purple-300">
              SOPs only
            </Badge>
          )}
        </div>
        {category.description && (
          <p className="text-sm text-gray-500 dark:text-gray-400 truncate">
            {category.description}
          </p>
        )}
      </div>
      
      <div className="flex items-center gap-1">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => onEdit(category)}
          data-testid={`button-edit-category-${category.id}`}
        >
          <Edit className="w-4 h-4" />
        </Button>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => onDelete(category.id)}
          className="text-red-600 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300"
          data-testid={`button-delete-category-${category.id}`}
        >
          <Trash2 className="w-4 h-4" />
        </Button>
      </div>
    </div>
  );
}

interface CategoryFormProps {
  category?: Category;
  onSave: (data: NewCategory) => void;
  onCancel: () => void;
}

const CATEGORY_SCOPE_OPTIONS = [
  { value: "all", label: "All features", description: "Available everywhere (Goals, Journal, Habits, SOPs, etc.)" },
  { value: "goals", label: "Goals only", description: "Only appears in the Goals section" },
  { value: "sops", label: "SOPs only", description: "Only appears when creating or filtering SOPs" },
] as const;

function CategoryForm({ category, onSave, onCancel }: CategoryFormProps) {
  const [name, setName] = useState(category?.name || "");
  const [description, setDescription] = useState(category?.description || "");
  const [color, setColor] = useState(category?.color || "#3b82f6");
  const [scope, setScope] = useState<"all" | "goals" | "sops">((category?.scope as any) || "all");

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) return;

    onSave({
      name: name.trim(),
      description: description.trim() || undefined,
      color,
      scope,
    });
  };

  return (
    <Card className="mb-6">
      <CardHeader>
        <CardTitle className="text-lg">
          {category ? "Edit Category" : "Add New Category"}
        </CardTitle>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Label htmlFor="category-name">Name</Label>
            <Input
              id="category-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Enter category name"
              required
              data-testid="input-category-name"
            />
          </div>
          
          <div>
            <Label htmlFor="category-description">Description (Optional)</Label>
            <Textarea
              id="category-description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Enter category description"
              rows={2}
              data-testid="input-category-description"
            />
          </div>
          
          <div>
            <Label htmlFor="category-color">Color</Label>
            <div className="flex items-center gap-3 mt-1">
              <input
                type="color"
                id="category-color"
                value={color}
                onChange={(e) => setColor(e.target.value)}
                className="w-12 h-8 rounded border border-gray-300 dark:border-gray-600"
                data-testid="input-category-color"
              />
              <Input
                value={color}
                onChange={(e) => setColor(e.target.value)}
                placeholder="#3b82f6"
                className="flex-1"
              />
            </div>
          </div>

          <div>
            <Label htmlFor="category-scope">Available in</Label>
            <Select value={scope} onValueChange={(v: any) => setScope(v)}>
              <SelectTrigger id="category-scope" data-testid="select-category-scope" className="mt-1">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {CATEGORY_SCOPE_OPTIONS.map((opt) => (
                  <SelectItem key={opt.value} value={opt.value}>
                    <span className="font-medium">{opt.label}</span>
                    <span className="ml-2 text-xs text-muted-foreground">{opt.description}</span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          
          <div className="flex items-center gap-2">
            <Button type="submit" data-testid="button-save-category">
              <Save className="w-4 h-4 mr-2" />
              {category ? "Update" : "Add"} Category
            </Button>
            <Button type="button" variant="outline" onClick={onCancel} data-testid="button-cancel-category">
              <X className="w-4 h-4 mr-2" />
              Cancel
            </Button>
          </div>
        </form>
      </CardContent>
    </Card>
  );
}

interface AdminSuggestion {
  id: string;
  content: any; // Rich text JSON content (Tiptap)
  plainContent: string; // Plain text for display
  status: string;
  userName: string;
  userEmail: string;
  tenantName: string;
  createdAt: Date;
  updatedAt: Date;
}

// ─── Audit Log Section ─────────────────────────────────────────────────────
interface AuditLogEntry {
  id: string;
  tenantId: string | null;
  actorUserId: string | null;
  actorEmail: string | null;
  action: string;
  targetType: string | null;
  targetId: string | null;
  targetDescription: string | null;
  beforeValue: any;
  afterValue: any;
  ipAddress: string | null;
  createdAt: string;
}

function AuditLogSection() {
  const { toast } = useToast();
  const [filterAction, setFilterAction] = useState("");
  const [filterTenantId, setFilterTenantId] = useState("");
  const [page, setPage] = useState(0);
  const [expandedLog, setExpandedLog] = useState<string | null>(null);
  const PAGE_SIZE = 50;

  const { data, isLoading, isError, refetch } = useQuery<{ logs: AuditLogEntry[]; total: number }>({
    queryKey: ["/api/admin/audit-logs", { action: filterAction, tenantId: filterTenantId, offset: page * PAGE_SIZE }],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (filterAction) params.set("action", filterAction);
      if (filterTenantId) params.set("tenantId", filterTenantId);
      params.set("limit", String(PAGE_SIZE));
      params.set("offset", String(page * PAGE_SIZE));
      const res = await fetch(`/api/admin/audit-logs?${params}`, { credentials: "include" });
      if (!res.ok) {
        const body = await res.json().catch(() => null);
        throw new Error(body?.error ?? `Failed to load audit logs (${res.status})`);
      }
      return res.json();
    },
  });

  const logs = data?.logs ?? [];
  const total = data?.total ?? 0;

  return (
    <div className="mt-8">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-xl font-bold flex items-center gap-2">
          <ClipboardList className="w-5 h-5" />
          Audit Log
        </h2>
        <Button variant="outline" size="sm" onClick={() => refetch()}>
          <RefreshCw className="w-4 h-4 mr-2" />Refresh
        </Button>
      </div>

      <div className="flex gap-3 mb-4">
        <Input placeholder="Filter by action (e.g. tenant.freeze)" value={filterAction} onChange={e => { setFilterAction(e.target.value); setPage(0); }} className="max-w-xs" />
        <Input placeholder="Filter by tenant ID" value={filterTenantId} onChange={e => { setFilterTenantId(e.target.value); setPage(0); }} className="max-w-xs" />
      </div>

      {isLoading ? (
        <div className="space-y-2">{[1,2,3,4,5].map(i=><div key={i} className="h-10 bg-muted animate-pulse rounded"/>)}</div>
      ) : isError ? (
        <div className="text-center py-8 border rounded-lg" data-testid="error-audit-logs">
          <XCircle className="w-10 h-10 mx-auto mb-2 text-destructive opacity-70" />
          <p className="text-sm text-destructive mb-3">Failed to load audit logs.</p>
          <Button variant="outline" size="sm" onClick={() => refetch()} data-testid="button-retry-audit-logs">
            <RefreshCw className="w-4 h-4 mr-2" />Retry
          </Button>
        </div>
      ) : logs.length === 0 ? (
        <div className="text-center py-8 text-muted-foreground border rounded-lg">
          <ClipboardList className="w-10 h-10 mx-auto mb-2 opacity-30" />
          <p>No audit log entries found.</p>
        </div>
      ) : (
        <>
          <div className="border rounded-lg overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-muted/50">
                <tr>
                  <th className="text-left p-3 font-medium">Timestamp</th>
                  <th className="text-left p-3 font-medium">Actor</th>
                  <th className="text-left p-3 font-medium">Action</th>
                  <th className="text-left p-3 font-medium">Target</th>
                  <th className="text-left p-3 font-medium">IP</th>
                  <th className="text-left p-3 font-medium">Details</th>
                </tr>
              </thead>
              <tbody>
                {logs.map(log => (
                  <>
                    <tr key={log.id} className="border-t hover:bg-muted/30 transition-colors">
                      <td className="p-3 text-muted-foreground whitespace-nowrap">
                        {format(new Date(log.createdAt), "MMM d, HH:mm:ss")}
                      </td>
                      <td className="p-3">
                        <div className="truncate max-w-[140px]" title={log.actorEmail ?? log.actorUserId ?? "System"}>
                          {log.actorEmail ?? log.actorUserId ?? <span className="text-muted-foreground">System</span>}
                        </div>
                      </td>
                      <td className="p-3">
                        <code className="text-xs bg-muted px-1.5 py-0.5 rounded">{log.action}</code>
                      </td>
                      <td className="p-3 text-muted-foreground">
                        <span className="truncate max-w-[120px] block" title={log.targetDescription ?? log.targetId ?? ""}>
                          {log.targetDescription ?? log.targetId ?? "—"}
                        </span>
                      </td>
                      <td className="p-3 text-muted-foreground text-xs">{log.ipAddress ?? "—"}</td>
                      <td className="p-3">
                        {(log.beforeValue || log.afterValue) && (
                          <Button variant="ghost" size="sm" className="h-6 w-6 p-0" onClick={() => setExpandedLog(expandedLog === log.id ? null : log.id)}>
                            {expandedLog === log.id ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
                          </Button>
                        )}
                      </td>
                    </tr>
                    {expandedLog === log.id && (
                      <tr key={`${log.id}-detail`} className="bg-muted/20 border-t">
                        <td colSpan={6} className="p-4">
                          <div className="grid grid-cols-2 gap-4 text-xs font-mono">
                            {log.beforeValue && (
                              <div>
                                <p className="font-semibold text-muted-foreground mb-1">Before</p>
                                <pre className="bg-muted p-2 rounded overflow-auto max-h-32">{JSON.stringify(log.beforeValue, null, 2)}</pre>
                              </div>
                            )}
                            {log.afterValue && (
                              <div>
                                <p className="font-semibold text-muted-foreground mb-1">After</p>
                                <pre className="bg-muted p-2 rounded overflow-auto max-h-32">{JSON.stringify(log.afterValue, null, 2)}</pre>
                              </div>
                            )}
                          </div>
                        </td>
                      </tr>
                    )}
                  </>
                ))}
              </tbody>
            </table>
          </div>
          <div className="flex items-center justify-between mt-3 text-sm text-muted-foreground">
            <span>Showing {page * PAGE_SIZE + 1}–{Math.min((page + 1) * PAGE_SIZE, total)} of {total}</span>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" disabled={page === 0} onClick={() => setPage(p => p - 1)}>Previous</Button>
              <Button variant="outline" size="sm" disabled={(page + 1) * PAGE_SIZE >= total} onClick={() => setPage(p => p + 1)}>Next</Button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

// ─── Manus Task Reclaim Section (master admin) ──────────────────────────────
interface ManusTaskOwner {
  tenantId: string;
  tenantName: string | null;
  userId: string;
  userName: string | null;
  userEmail: string | null;
}

interface UnownedManusTask {
  id: string;
  title: string | null;
  status: string | null;
  createdAt: number | null;
  updatedAt: number | null;
  owner?: ManusTaskOwner;
}

interface AdminTenantWithUsers {
  id: string;
  name: string;
  users: Array<{ id: string; name: string; email: string; role: string }>;
}

function ManusReclaimSection() {
  const { toast } = useToast();
  const [selectedTaskIds, setSelectedTaskIds] = useState<Set<string>>(new Set());
  const [targetTenantId, setTargetTenantId] = useState("");
  const [targetUserId, setTargetUserId] = useState("");
  const [includeOwned, setIncludeOwned] = useState(false);
  const [confirmReassignOpen, setConfirmReassignOpen] = useState(false);
  const [filterText, setFilterText] = useState("");

  const { data, isLoading, isError, error, refetch, isFetching } = useQuery<{
    unowned: UnownedManusTask[];
    owned?: UnownedManusTask[];
    totalFetched: number;
    ownedCount: number;
  }>({
    queryKey: ["/api/admin/manus/tasks/unowned", { includeOwned }],
    queryFn: async () => {
      const res = await fetch(`/api/admin/manus/tasks/unowned${includeOwned ? "?includeOwned=true" : ""}`, { credentials: "include" });
      if (!res.ok) {
        const body = await res.json().catch(() => null);
        const err = new Error(body?.error ?? "Failed to load unowned agent tasks") as Error & { status?: number };
        err.status = res.status;
        throw err;
      }
      return res.json();
    },
    // Hits the external Manus API — only load on demand/mount, don't poll.
    staleTime: 60_000,
  });

  const { data: tenantsData } = useQuery<AdminTenantWithUsers[]>({
    queryKey: ["/api/admin/tenants"],
    queryFn: async () => {
      const res = await fetch("/api/admin/tenants", { credentials: "include" });
      if (!res.ok) throw new Error("Failed to load tenants");
      return res.json();
    },
  });

  const unowned = data?.unowned ?? [];
  const owned = data?.owned ?? [];
  const allTasks = includeOwned ? [...unowned, ...owned] : unowned;
  const normalizedFilter = filterText.trim().toLowerCase();
  const displayTasks = normalizedFilter
    ? allTasks.filter((t) => {
        const haystack = [
          t.title,
          t.id,
          t.owner?.tenantName,
          t.owner?.tenantId,
          t.owner?.userName,
          t.owner?.userEmail,
          t.owner?.userId,
        ];
        return haystack.some((v) => v?.toLowerCase().includes(normalizedFilter));
      })
    : allTasks;
  const tenants = tenantsData ?? [];
  const selectedTenant = tenants.find((t) => t.id === targetTenantId);
  const tenantUsers = selectedTenant?.users?.filter((u) => u.id) ?? [];

  const assignMutation = useMutation({
    mutationFn: async () => {
      const res = await apiRequest("POST", "/api/admin/manus/tasks/assign", {
        taskIds: Array.from(selectedTaskIds),
        tenantId: targetTenantId,
        userId: targetUserId,
      });
      return res.json();
    },
    onSuccess: (result: { assigned: number }) => {
      toast({
        title: "Tasks assigned",
        description: `${result.assigned} agent task(s) assigned to ${selectedTenant?.name ?? targetTenantId}.`,
      });
      setSelectedTaskIds(new Set());
      setConfirmReassignOpen(false);
      queryClient.invalidateQueries({ queryKey: ["/api/admin/manus/tasks/unowned"] });
    },
    onError: (err: Error) => {
      toast({ title: "Assignment failed", description: err.message, variant: "destructive" });
    },
  });

  // Derived from allTasks (not displayTasks) so owned selections hidden by the
  // filter still trigger the reassign confirmation dialog.
  const selectedOwnedTasks = allTasks.filter((t) => selectedTaskIds.has(t.id) && t.owner);

  const handleAssignClick = () => {
    if (selectedOwnedTasks.length > 0) {
      // Reassigning already-owned tasks requires explicit confirmation
      // showing old → new owner to prevent accidental cross-tenant moves.
      setConfirmReassignOpen(true);
    } else {
      assignMutation.mutate();
    }
  };

  const toggleTask = (id: string) => {
    setSelectedTaskIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const allSelected = displayTasks.length > 0 && displayTasks.every((t) => selectedTaskIds.has(t.id));

  const ownerLabel = (owner: ManusTaskOwner) => {
    const user = owner.userName || owner.userEmail || owner.userId;
    const tenant = owner.tenantName || owner.tenantId;
    return `${tenant} / ${user}`;
  };

  return (
    <div className="mt-8" data-testid="section-manus-reclaim">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-xl font-bold flex items-center gap-2">
          <Sparkles className="w-5 h-5" />
          Reclaim Legacy Agent Tasks
        </h2>
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2">
            <Switch
              id="switch-include-owned-manus"
              checked={includeOwned}
              onCheckedChange={(v) => { setIncludeOwned(v); setSelectedTaskIds(new Set()); }}
              data-testid="switch-include-owned-manus"
            />
            <Label htmlFor="switch-include-owned-manus" className="text-sm cursor-pointer">Include owned tasks</Label>
          </div>
          <Button variant="outline" size="sm" onClick={() => refetch()} disabled={isFetching} data-testid="button-refresh-unowned-manus">
            <RefreshCw className={`w-4 h-4 mr-2 ${isFetching ? "animate-spin" : ""}`} />Refresh
          </Button>
        </div>
      </div>
      <p className="text-sm text-muted-foreground mb-4">
        Agent tasks created before ownership tracking have no owner and are hidden from all users.
        Select tasks below, pick a tenant and user, and assign them so they reappear in that user's task list.
        {includeOwned && " Owned tasks are also shown with their current owner — reassigning one requires confirmation."}
      </p>

      {isLoading ? (
        <div className="space-y-2">{[1, 2, 3].map((i) => <div key={i} className="h-10 bg-muted animate-pulse rounded" />)}</div>
      ) : isError && (error as Error & { status?: number })?.status === 503 ? (
        <div className="text-center py-8 text-muted-foreground border rounded-lg" data-testid="text-manus-not-configured">
          <Lock className="w-10 h-10 mx-auto mb-2 opacity-40" />
          <p className="font-medium text-foreground mb-1">Agent service is not configured</p>
          <p className="text-sm max-w-md mx-auto">
            The <code className="bg-muted px-1 py-0.5 rounded text-xs">MANUS_API_KEY</code> secret is missing,
            so agent tasks can't be loaded. Add it in your workspace's Secrets settings, then restart the
            API server and refresh this page.
          </p>
        </div>
      ) : isError ? (
        <div className="text-center py-8 text-destructive border rounded-lg" data-testid="text-unowned-manus-error">
          <XCircle className="w-10 h-10 mx-auto mb-2 opacity-50" />
          <p>{(error as Error)?.message ?? "Failed to load unowned agent tasks."}</p>
        </div>
      ) : allTasks.length === 0 ? (
        <div className="text-center py-8 text-muted-foreground border rounded-lg" data-testid="text-no-unowned-manus">
          <CheckCircle2 className="w-10 h-10 mx-auto mb-2 opacity-30" />
          <p>No unowned agent tasks found. All {data?.totalFetched ?? 0} tasks have owners.</p>
        </div>
      ) : (
        <>
          <div className="relative mb-3 max-w-sm">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
            <Input
              value={filterText}
              onChange={(e) => setFilterText(e.target.value)}
              placeholder="Filter by title, task ID, tenant, or user"
              className="pl-9"
              data-testid="input-filter-manus-tasks"
            />
          </div>
          {displayTasks.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground border rounded-lg mb-4" data-testid="text-no-filtered-manus">
              <Search className="w-10 h-10 mx-auto mb-2 opacity-30" />
              <p>No tasks match "{filterText.trim()}".</p>
            </div>
          ) : (
          <div className="border rounded-lg overflow-hidden mb-4">
            <table className="w-full text-sm">
              <thead className="bg-muted/50">
                <tr>
                  <th className="p-3 w-10">
                    <input
                      type="checkbox"
                      checked={allSelected}
                      onChange={() => setSelectedTaskIds((prev) => {
                        const next = new Set(prev);
                        if (allSelected) displayTasks.forEach((t) => next.delete(t.id));
                        else displayTasks.forEach((t) => next.add(t.id));
                        return next;
                      })}
                      data-testid="checkbox-select-all-unowned"
                    />
                  </th>
                  <th className="text-left p-3 font-medium">Task ID</th>
                  <th className="text-left p-3 font-medium">Title</th>
                  <th className="text-left p-3 font-medium">Status</th>
                  <th className="text-left p-3 font-medium">Created</th>
                  {includeOwned && <th className="text-left p-3 font-medium">Current Owner</th>}
                </tr>
              </thead>
              <tbody>
                {displayTasks.map((task) => (
                  <tr key={task.id} className="border-t hover:bg-muted/30 transition-colors cursor-pointer" onClick={() => toggleTask(task.id)} data-testid={`row-unowned-task-${task.id}`}>
                    <td className="p-3">
                      <input
                        type="checkbox"
                        checked={selectedTaskIds.has(task.id)}
                        onChange={() => toggleTask(task.id)}
                        onClick={(e) => e.stopPropagation()}
                        data-testid={`checkbox-unowned-task-${task.id}`}
                      />
                    </td>
                    <td className="p-3"><code className="text-xs bg-muted px-1.5 py-0.5 rounded">{task.id}</code></td>
                    <td className="p-3">
                      <span className="truncate max-w-[240px] block" title={task.title ?? ""}>{task.title ?? <span className="text-muted-foreground">Untitled</span>}</span>
                    </td>
                    <td className="p-3">{task.status ? <Badge variant="outline">{task.status}</Badge> : "—"}</td>
                    <td className="p-3 text-muted-foreground whitespace-nowrap">
                      {task.createdAt ? format(new Date(task.createdAt * 1000), "MMM d, yyyy HH:mm") : "—"}
                    </td>
                    {includeOwned && (
                      <td className="p-3" data-testid={`text-task-owner-${task.id}`}>
                        {task.owner ? (
                          <div className="text-xs">
                            <div className="font-medium">{task.owner.tenantName ?? task.owner.tenantId}</div>
                            <div className="text-muted-foreground">{task.owner.userName || task.owner.userEmail || task.owner.userId}</div>
                          </div>
                        ) : (
                          <Badge variant="secondary" className="text-xs">Unowned</Badge>
                        )}
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          )}

          <div className="flex flex-wrap items-end gap-3">
            <div>
              <Label className="text-xs mb-1 block">Tenant</Label>
              <Select value={targetTenantId} onValueChange={(v) => { setTargetTenantId(v); setTargetUserId(""); }}>
                <SelectTrigger className="w-[220px]" data-testid="select-reclaim-tenant">
                  <SelectValue placeholder="Select tenant" />
                </SelectTrigger>
                <SelectContent>
                  {tenants.filter((t) => t.id).map((t) => (
                    <SelectItem key={t.id} value={t.id}>{t.name || t.id}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs mb-1 block">User</Label>
              <Select value={targetUserId} onValueChange={setTargetUserId} disabled={!targetTenantId}>
                <SelectTrigger className="w-[260px]" data-testid="select-reclaim-user">
                  <SelectValue placeholder={targetTenantId ? "Select user" : "Select a tenant first"} />
                </SelectTrigger>
                <SelectContent>
                  {tenantUsers.map((u) => (
                    <SelectItem key={u.id} value={u.id}>{u.name}{u.email ? ` (${u.email})` : ""}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <Button
              onClick={handleAssignClick}
              disabled={selectedTaskIds.size === 0 || !targetTenantId || !targetUserId || assignMutation.isPending}
              data-testid="button-assign-manus-tasks"
            >
              {assignMutation.isPending ? "Assigning…" : `Assign ${selectedTaskIds.size} task(s)`}
            </Button>
          </div>
        </>
      )}

      <Dialog open={confirmReassignOpen} onOpenChange={setConfirmReassignOpen}>
        <DialogContent data-testid="dialog-confirm-reassign-manus">
          <DialogHeader>
            <DialogTitle>Reassign owned agent tasks?</DialogTitle>
            <DialogDescription>
              {selectedOwnedTasks.length} of the selected task(s) already have an owner.
              Confirm moving them to the new owner below.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2 text-sm">
            {selectedOwnedTasks.map((task) => (
              <div key={task.id} className="border rounded-lg p-3" data-testid={`row-reassign-task-${task.id}`}>
                <div className="font-medium truncate mb-1">{task.title ?? task.id}</div>
                <div className="flex items-center gap-2 flex-wrap text-xs">
                  <span className="text-muted-foreground">{task.owner ? ownerLabel(task.owner) : "Unowned"}</span>
                  <span aria-hidden>→</span>
                  <span className="font-medium">
                    {(selectedTenant?.name || targetTenantId)} / {(() => {
                      const u = tenantUsers.find((u) => u.id === targetUserId);
                      return u ? (u.name || u.email || u.id) : targetUserId;
                    })()}
                  </span>
                </div>
              </div>
            ))}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmReassignOpen(false)} data-testid="button-cancel-reassign-manus">
              Cancel
            </Button>
            <Button
              onClick={() => assignMutation.mutate()}
              disabled={assignMutation.isPending}
              data-testid="button-confirm-reassign-manus"
            >
              {assignMutation.isPending ? "Reassigning…" : `Reassign ${selectedTaskIds.size} task(s)`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ─── Mobile App Section ──────────────────────────────────────────────────────
const MOBILE_FEATURE_LABELS: Record<string, string> = {
  dashboard: "Dashboard",
  goals: "Goals",
  tasks: "Tasks",
  habits: "Habits",
  journal: "Journal",
  calendar: "Calendar / Planner",
  meditation: "Meditation",
  notepad: "Notepad",
  community: "Community",
  planner: "Planner",
  gratefuls: "Gratefuls",
  timeBudget: "Time Budget",
  aiCoach: "AI Coach",
  agentTasks: "Agent Tasks",
  aiCreate: "AI Create",
  healthMetrics: "Health Metrics",
  workoutPlanner: "Workout Planner",
  mealPlanner: "Meal Planner",
  media: "Media",
  drive: "Drive",
  achievements: "Achievements",
  suggestions: "Suggestions",
  trackerx: "TrackerX",
  visionBoard: "Vision Board",
};

function MobileAppSection() {
  const { toast } = useToast();
  const [localFlags, setLocalFlags] = useState<Record<string, boolean> | null>(null);
  const [showConflictBanner, setShowConflictBanner] = useState(false);
  const baseFlagsRef = useRef<Record<string, boolean> | null>(null);
  const prevFlagsRef = useRef<Record<string, boolean> | undefined>(undefined);

  const { data: flags, isLoading, refetch } = useQuery<Record<string, boolean>>({
    queryKey: ["/api/mobile/features"],
    queryFn: getQueryFn({ on401: "returnNull" }),
    refetchOnWindowFocus: true,
    refetchInterval: 30_000,
  });

  useEffect(() => {
    if (flags === undefined) return;
    const prev = prevFlagsRef.current;
    prevFlagsRef.current = flags;
    if (prev === undefined) return;
    if (localFlags === null || baseFlagsRef.current === null) return;
    const base = baseFlagsRef.current;
    const changed = Object.keys({ ...base, ...flags }).some(
      (k) => (base[k] ?? true) !== (flags[k] ?? true)
    );
    if (changed) {
      setShowConflictBanner(true);
    }
  }, [flags]);

  const updateMutation = useMutation({
    mutationFn: async (patch: Record<string, boolean>) => {
      const res = await fetch("/api/admin/mobile/features", {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(patch),
      });
      if (!res.ok) throw new Error("Failed to update");
      return res.json();
    },
    onSuccess: () => {
      toast({ title: "Mobile features updated", description: "Changes are now live for all mobile users." });
      setLocalFlags(null);
      baseFlagsRef.current = null;
      setShowConflictBanner(false);
      refetch();
    },
    onError: () => {
      toast({ title: "Error", description: "Failed to update mobile features.", variant: "destructive" });
    },
  });

  const current = localFlags ?? flags ?? {};

  const toggle = (key: string) => {
    setLocalFlags((prev) => {
      if (prev === null && flags !== undefined) {
        baseFlagsRef.current = { ...flags };
      }
      const base = prev ?? flags ?? {};
      return { ...base, [key]: !base[key] };
    });
  };

  const handleSave = () => {
    if (!localFlags) return;
    updateMutation.mutate(localFlags);
  };

  const handleReload = () => {
    setLocalFlags(null);
    baseFlagsRef.current = null;
    setShowConflictBanner(false);
  };

  const handleKeepEdits = () => {
    if (flags !== undefined) {
      baseFlagsRef.current = { ...flags };
    }
    setShowConflictBanner(false);
  };

  const isDirty = !!localFlags;

  return (
    <Card className="mt-6">
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2">
              <Settings2 className="w-5 h-5" />
              Mobile App Features
            </CardTitle>
            <CardDescription>
              Control which features are visible to all users in the GoalVia mobile app.
            </CardDescription>
          </div>
          {isDirty && (
            <Button onClick={handleSave} disabled={updateMutation.isPending} size="sm">
              <Save className="w-4 h-4 mr-2" />
              {updateMutation.isPending ? "Saving..." : "Save Changes"}
            </Button>
          )}
        </div>
      </CardHeader>
      <CardContent>
        {showConflictBanner && (
          <div className="flex items-start gap-3 rounded-lg border border-yellow-200 bg-yellow-50 dark:border-yellow-800 dark:bg-yellow-950/30 px-4 py-3 mb-4">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="w-5 h-5 text-yellow-600 dark:text-yellow-400 shrink-0 mt-0.5"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
              <line x1="12" y1="9" x2="12" y2="13" />
              <line x1="12" y1="17" x2="12.01" y2="17" />
            </svg>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-yellow-800 dark:text-yellow-200">
                Someone else changed these settings — reload to see the latest?
              </p>
              <p className="text-xs text-yellow-700 dark:text-yellow-300 mt-0.5">
                A background refresh detected server-side changes while you have unsaved edits.
              </p>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <Button
                variant="outline"
                size="sm"
                className="text-yellow-800 border-yellow-300 hover:bg-yellow-100 dark:text-yellow-200 dark:border-yellow-700 dark:hover:bg-yellow-900/50 h-7 text-xs"
                onClick={handleKeepEdits}
              >
                Keep my edits
              </Button>
              <Button
                size="sm"
                className="bg-yellow-600 hover:bg-yellow-700 text-white h-7 text-xs"
                onClick={handleReload}
              >
                Reload
              </Button>
            </div>
          </div>
        )}
        {isLoading ? (
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            {Array.from({ length: 12 }).map((_, i) => (
              <Skeleton key={i} className="h-10 w-full" />
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            {Object.entries(MOBILE_FEATURE_LABELS).map(([key, label]) => (
              <div
                key={key}
                className="flex items-center justify-between rounded-lg border p-3 bg-muted/30"
              >
                <span className="text-sm font-medium">{label}</span>
                <Switch
                  checked={!!(current[key] ?? true)}
                  onCheckedChange={() => toggle(key)}
                />
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// ─── Dunning Config Section ─────────────────────────────────────────────────
interface DunningConfig {
  id?: string;
  retryDays: number[];
  gracePeriodDays: number;
  stageActions: Record<string, string>;
  isActive: boolean;
}

// Per-section error boundary so one broken master-admin widget doesn't blank the whole page
class SectionErrorBoundary extends Component<
  { title: string; children: ReactNode },
  { hasError: boolean; error: Error | null }
> {
  constructor(props: { title: string; children: ReactNode }) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error) {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error(`Admin section "${this.props.title}" crashed:`, error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="mt-8 border border-destructive/30 rounded-lg p-4 bg-destructive/5">
          <p className="text-sm font-medium text-destructive mb-1">
            {this.props.title} failed to load
          </p>
          <p className="text-xs text-muted-foreground font-mono">
            {this.state.error?.message ?? "Unknown error"}
          </p>
          <Button
            variant="outline"
            size="sm"
            className="mt-3"
            onClick={() => this.setState({ hasError: false, error: null })}
          >
            <RefreshCw className="w-3 h-3 mr-2" />
            Retry
          </Button>
        </div>
      );
    }
    return this.props.children;
  }
}

export function AiUsageSection() {
  const now = new Date();
  const defaultMonth = now.toISOString().slice(0, 7);
  const [month, setMonth] = useState(defaultMonth);
  const [expandedTenantId, setExpandedTenantId] = useState<string | null>(null);

  const { data, isLoading, isError, refetch } = useQuery<{
    month: string;
    tenants: Array<{
      tenantId: string;
      tenantName: string;
      planType: string;
      tokensUsed: number;
      cap: number | null;
      pctUsed: number;
      estimatedCost: number;
      topFeatures: Array<{ feature: string; tokens: number }>;
    }>;
    dailyUsage: Array<{ date: string; tokens: number }>;
    topFeatures: Array<{ feature: string; tokens: number }>;
    totalTokens: number;
    totalEstimatedCost: number;
  }>({
    queryKey: ["/api/admin/ai-usage", month],
    queryFn: async () => {
      const res = await fetch(`/api/admin/ai-usage?month=${month}`, { credentials: "include" });
      if (!res.ok) {
        const body = await res.json().catch(() => null);
        throw new Error(body?.error ?? `Failed to load AI usage data (${res.status})`);
      }
      return res.json();
    },
    staleTime: 60_000,
  });

  const monthOptions: string[] = [];
  for (let i = 0; i < 6; i++) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    monthOptions.push(d.toISOString().slice(0, 7));
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <CardTitle className="flex items-center gap-2">
              <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><path d="M12 8v4l3 3"/></svg>
              AI Usage Dashboard
            </CardTitle>
            <CardDescription>Per-tenant monthly AI token consumption and cost estimates</CardDescription>
          </div>
          <div className="flex items-center gap-2">
            <Select value={month} onValueChange={setMonth} data-testid="select-ai-usage-month">
              <SelectTrigger className="w-36">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {monthOptions.map(m => (
                  <SelectItem key={m} value={m}>{m}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button size="sm" variant="outline" onClick={() => refetch()}>
              Refresh
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <div className="space-y-3">
            <div className="h-8 bg-muted animate-pulse rounded" />
            <div className="h-8 bg-muted animate-pulse rounded" />
            <div className="h-8 bg-muted animate-pulse rounded" />
          </div>
        ) : isError ? (
          <p className="text-sm text-destructive">Failed to load AI usage data.</p>
        ) : !data || (data.tenants ?? []).length === 0 ? (
          <p className="text-sm text-muted-foreground">No AI usage recorded for {month}.</p>
        ) : (
          <div className="space-y-6">
            {/* Summary row */}
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              <div className="bg-muted/40 rounded-lg p-4">
                <p className="text-xs text-muted-foreground mb-1">Total tokens</p>
                <p className="text-2xl font-bold">{(data.totalTokens ?? 0).toLocaleString()}</p>
              </div>
              <div className="bg-muted/40 rounded-lg p-4">
                <p className="text-xs text-muted-foreground mb-1">Est. cost</p>
                <p className="text-2xl font-bold">${(data.totalEstimatedCost ?? 0).toFixed(2)}</p>
              </div>
              <div className="bg-muted/40 rounded-lg p-4">
                <p className="text-xs text-muted-foreground mb-1">Active tenants</p>
                <p className="text-2xl font-bold">{(data.tenants ?? []).length}</p>
              </div>
            </div>

            {/* Top features globally */}
            {(data.topFeatures ?? []).length > 0 && (
              <div>
                <p className="text-sm font-medium mb-3">Top features by token usage</p>
                <div className="space-y-2">
                  {(data.topFeatures ?? []).map(f => {
                    const pct = data.totalTokens > 0 ? (f.tokens / data.totalTokens) * 100 : 0;
                    return (
                      <div key={f.feature} className="flex items-center gap-3">
                        <p className="text-xs text-muted-foreground w-44 truncate">{formatFeatureName(f.feature)}</p>
                        <div className="flex-1 bg-muted rounded-full h-2 overflow-hidden">
                          <div className="bg-primary h-2 rounded-full" style={{ width: `${Math.min(100, pct)}%` }} />
                        </div>
                        <p className="text-xs tabular-nums w-24 text-right">{f.tokens.toLocaleString()} tokens</p>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Daily usage bar chart (simple CSS bars) */}
            {(data.dailyUsage ?? []).length > 0 && (
              <div>
                <p className="text-sm font-medium mb-3">Daily usage — {month}</p>
                <div className="flex items-end gap-0.5 h-24 overflow-x-auto">
                  {(() => {
                    const daily = data.dailyUsage ?? [];
                    const max = Math.max(...daily.map(d => d.tokens), 1);
                    return daily.map(d => {
                      const pct = (d.tokens / max) * 100;
                      return (
                        <div
                          key={d.date}
                          className="flex-1 min-w-[6px] bg-primary/70 rounded-t hover:bg-primary transition-colors cursor-default"
                          style={{ height: `${Math.max(4, pct)}%` }}
                          title={`${d.date}: ${d.tokens.toLocaleString()} tokens`}
                        />
                      );
                    });
                  })()}
                </div>
              </div>
            )}

            {/* Per-tenant table */}
            <div>
              <p className="text-sm font-medium mb-3">Per-tenant breakdown</p>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b text-left text-muted-foreground text-xs">
                      <th className="pb-2 pr-3">Tenant</th>
                      <th className="pb-2 pr-3">Plan</th>
                      <th className="pb-2 pr-3 text-right">Tokens used</th>
                      <th className="pb-2 pr-3 text-right">Cap</th>
                      <th className="pb-2 pr-3">Usage</th>
                      <th className="pb-2 text-right">Est. cost</th>
                      <th className="pb-2 pl-3 text-right">Features</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(data.tenants ?? []).map(t => (
                      <TenantUsageRow
                        key={t.tenantId}
                        tenant={t}
                        expanded={expandedTenantId === t.tenantId}
                        onToggle={() =>
                          setExpandedTenantId(prev => (prev === t.tenantId ? null : t.tenantId))
                        }
                      />
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

interface TenantUsageRowProps {
  tenant: {
    tenantId: string;
    tenantName: string;
    planType: string;
    tokensUsed: number;
    cap: number | null;
    pctUsed: number;
    estimatedCost: number;
    topFeatures: Array<{ feature: string; tokens: number }>;
  };
  expanded: boolean;
  onToggle: () => void;
}

export function TenantUsageRow({ tenant: t, expanded, onToggle }: TenantUsageRowProps) {
  const topFeatures = t.topFeatures ?? [];
  return (
    <>
      <tr className="border-b last:border-0">
        <td className="py-2 pr-3 font-medium">{t.tenantName}</td>
        <td className="py-2 pr-3 text-muted-foreground capitalize">{t.planType}</td>
        <td className="py-2 pr-3 text-right tabular-nums">{t.tokensUsed.toLocaleString()}</td>
        <td className="py-2 pr-3 text-right tabular-nums text-muted-foreground">
          {t.cap === null ? "∞" : t.cap.toLocaleString()}
        </td>
        <td className="py-2 pr-3">
          {t.cap !== null ? (
            <div className="flex items-center gap-2">
              <div className="flex-1 bg-muted rounded-full h-1.5 min-w-[60px]">
                <div
                  className={`h-1.5 rounded-full ${t.pctUsed >= 100 ? "bg-destructive" : t.pctUsed >= 80 ? "bg-amber-500" : "bg-primary"}`}
                  style={{ width: `${Math.min(100, t.pctUsed)}%` }}
                />
              </div>
              <span className="text-xs tabular-nums w-8">{t.pctUsed}%</span>
            </div>
          ) : (
            <span className="text-xs text-muted-foreground">unlimited</span>
          )}
        </td>
        <td className="py-2 text-right tabular-nums">${t.estimatedCost.toFixed(4)}</td>
        <td className="py-2 pl-3 text-right">
          {topFeatures.length > 0 ? (
            <Button
              variant="ghost"
              size="sm"
              className="h-6 px-1.5"
              onClick={onToggle}
              aria-expanded={expanded}
              data-testid={`button-toggle-tenant-features-${t.tenantId}`}
            >
              {expanded ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
            </Button>
          ) : (
            <span className="text-xs text-muted-foreground">—</span>
          )}
        </td>
      </tr>
      {expanded && topFeatures.length > 0 && (
        <tr className="bg-muted/20 border-b last:border-0" data-testid={`row-tenant-features-${t.tenantId}`}>
          <td colSpan={7} className="p-3">
            <p className="text-xs font-medium text-muted-foreground mb-2">
              Top features — {t.tenantName}
            </p>
            <div className="space-y-1.5">
              {topFeatures.map(f => {
                const pct = t.tokensUsed > 0 ? (f.tokens / t.tokensUsed) * 100 : 0;
                return (
                  <div key={f.feature} className="flex items-center gap-3">
                    <p
                      className="text-xs text-muted-foreground w-44 truncate"
                      data-testid={`text-tenant-feature-${t.tenantId}-${f.feature}`}
                    >
                      {formatFeatureName(f.feature)}
                    </p>
                    <div className="flex-1 bg-muted rounded-full h-1.5 overflow-hidden">
                      <div className="bg-primary h-1.5 rounded-full" style={{ width: `${Math.min(100, pct)}%` }} />
                    </div>
                    <p className="text-xs tabular-nums w-24 text-right">{f.tokens.toLocaleString()} tokens</p>
                  </div>
                );
              })}
            </div>
          </td>
        </tr>
      )}
    </>
  );
}

function DunningConfigSection() {
  const { toast } = useToast();
  const [editing, setEditing] = useState(false);
  const [retryDaysText, setRetryDaysText] = useState("1, 3, 7, 14");
  const [gracePeriod, setGracePeriod] = useState(5);
  const [stageActions, setStageActions] = useState<Record<string, string>>({});

  const { data: config, isLoading, refetch } = useQuery<DunningConfig>({
    queryKey: ["/api/admin/dunning-config"],
    onSuccess: (d: DunningConfig) => {
      setRetryDaysText((d.retryDays ?? [1,3,7,14]).join(", "));
      setGracePeriod(d.gracePeriodDays ?? 5);
      setStageActions(d.stageActions ?? {});
    },
  } as any);

  const saveMutation = useMutation({
    mutationFn: async () => {
      const retryDays = retryDaysText.split(",").map(s => parseInt(s.trim())).filter(n => !isNaN(n));
      return apiRequest("PUT", "/api/admin/dunning-config", { retryDays, gracePeriodDays: gracePeriod, stageActions, isActive: true });
    },
    onSuccess: () => { refetch(); setEditing(false); toast({ title: "Dunning config saved" }); },
    onError: (e: any) => toast({ title: "Error", description: e.message, variant: "destructive" }),
  });

  const retryDays = config?.retryDays ?? [1, 3, 7, 14];

  return (
    <div className="mt-8">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-xl font-bold flex items-center gap-2">
          <Settings2 className="w-5 h-5" />
          Dunning Workflow
        </h2>
        {!editing && (
          <Button variant="outline" size="sm" onClick={() => {
            if (config) {
              setRetryDaysText((config.retryDays ?? [1,3,7,14]).join(", "));
              setGracePeriod(config.gracePeriodDays ?? 5);
              setStageActions(config.stageActions ?? {});
            }
            setEditing(true);
          }}>
            <Edit className="w-4 h-4 mr-2" />Edit Config
          </Button>
        )}
      </div>

      {isLoading ? (
        <div className="h-32 bg-muted animate-pulse rounded-lg" />
      ) : (
        <div className="border rounded-lg p-4 space-y-4">
          {!editing ? (
            <>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4 text-sm">
                <div>
                  <p className="text-muted-foreground font-medium mb-1">Retry Schedule (days after failure)</p>
                  <div className="flex gap-2 flex-wrap">
                    {retryDays.map((d, i) => (
                      <span key={i} className="bg-primary/10 text-primary px-2 py-0.5 rounded text-xs font-mono">Day {d}</span>
                    ))}
                  </div>
                </div>
                <div>
                  <p className="text-muted-foreground font-medium mb-1">Grace Period</p>
                  <p className="font-semibold">{config?.gracePeriodDays ?? 5} days</p>
                </div>
                <div>
                  <p className="text-muted-foreground font-medium mb-1">Status</p>
                  <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${config?.isActive ? "bg-green-100 text-green-800" : "bg-muted text-muted-foreground"}`}>
                    {config?.isActive ? "Active" : "Inactive"}
                  </span>
                </div>
              </div>
              {config?.stageActions && Object.keys(config.stageActions).length > 0 && (
                <div>
                  <p className="text-muted-foreground font-medium text-sm mb-2">Stage Actions</p>
                  <div className="flex gap-3 flex-wrap">
                    {retryDays.map((d, i) => (
                      <div key={i} className="text-xs border rounded px-2 py-1">
                        <span className="text-muted-foreground">Day {d}: </span>
                        <span className="capitalize font-medium">{config.stageActions?.[String(i)] ?? "notify"}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </>
          ) : (
            <div className="space-y-4">
              <div>
                <label className="text-sm font-medium">Retry Days (comma-separated)</label>
                <Input value={retryDaysText} onChange={e => setRetryDaysText(e.target.value)} placeholder="1, 3, 7, 14" className="mt-1 font-mono" />
                <p className="text-xs text-muted-foreground mt-1">Days after first failed payment to retry billing</p>
              </div>
              <div>
                <label className="text-sm font-medium">Grace Period (days)</label>
                <NumberField value={gracePeriod} onValueChange={v => setGracePeriod(v ?? 5)} min={1} max={30} emptyValue={5} className="mt-1 w-32" data-testid="input-grace-period-days" />
              </div>
              <div>
                <label className="text-sm font-medium block mb-2">Action per Stage</label>
                {retryDaysText.split(",").map(s => s.trim()).filter(s => s).map((day, i) => (
                  <div key={i} className="flex items-center gap-3 mb-2">
                    <span className="text-sm text-muted-foreground w-20">Day {day}:</span>
                    <Select value={stageActions[String(i)] ?? "notify"} onValueChange={v => setStageActions(prev => ({ ...prev, [String(i)]: v }))}>
                      <SelectTrigger className="w-40"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="notify">Notify Only</SelectItem>
                        <SelectItem value="restrict">Restrict Features</SelectItem>
                        <SelectItem value="suspend">Suspend Account</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                ))}
              </div>
              <div className="flex gap-2">
                <Button onClick={() => saveMutation.mutate()} disabled={saveMutation.isPending}>
                  {saveMutation.isPending ? "Saving..." : "Save Config"}
                </Button>
                <Button variant="outline" onClick={() => setEditing(false)}>Cancel</Button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function SessionSecurityCard({ userPreferences }: { userPreferences: any }) {
  const { toast } = useToast();
  const [enabled, setEnabled] = useState<boolean>(true);
  const [minutesInput, setMinutesInput] = useState<string>("20");
  const [initialized, setInitialized] = useState(false);

  // Sync local state from fetched preferences once
  useEffect(() => {
    if (userPreferences && !initialized) {
      setEnabled(userPreferences.inactivityTimeoutEnabled ?? true);
      setMinutesInput(String(userPreferences.inactivityTimeoutMinutes ?? 20));
      setInitialized(true);
    }
  }, [userPreferences, initialized]);

  const parsedMinutes = parseInt(minutesInput, 10);
  const clampedMinutes = Number.isNaN(parsedMinutes)
    ? 20
    : Math.max(1, Math.min(480, parsedMinutes));
  const showClampHint =
    minutesInput.trim() !== "" &&
    !Number.isNaN(parsedMinutes) &&
    (parsedMinutes < 1 || parsedMinutes > 480);
  const showEmptyHint = minutesInput.trim() === "" || Number.isNaN(parsedMinutes);

  const saveMutation = useMutation({
    mutationFn: () =>
      apiRequest("PATCH", "/api/user/preferences", {
        inactivityTimeoutEnabled: enabled,
        inactivityTimeoutMinutes: clampedMinutes,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/user/preferences"] });
      toast({ title: "Session security settings saved" });
    },
    onError: () => toast({ title: "Failed to save settings", variant: "destructive" }),
  });

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Shield className="w-5 h-5" />
          Session Security
        </CardTitle>
        <CardDescription>
          Control how your active session behaves. These settings apply to your account only.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Inactivity timeout toggle + duration */}
        <div className="space-y-4">
          <div className="flex items-center justify-between p-4 border rounded-lg">
            <div className="flex items-start gap-3">
              <Timer className="w-5 h-5 mt-0.5 text-muted-foreground shrink-0" />
              <div>
                <h3 className="font-medium">Auto-Logout on Inactivity</h3>
                <p className="text-sm text-muted-foreground">
                  Automatically sign you out after a period of no mouse or keyboard activity.
                  Protects your account if you leave your device unattended.
                </p>
              </div>
            </div>
            <Switch
              checked={enabled}
              onCheckedChange={setEnabled}
              data-testid="switch-inactivity-timeout"
            />
          </div>

          {enabled && (
            <div className="space-y-1 px-4">
              <div className="flex items-center gap-4">
                <label className="text-sm font-medium whitespace-nowrap">
                  Log out after
                </label>
                <Input
                  type="number"
                  min={1}
                  max={480}
                  value={minutesInput}
                  onChange={(e) => setMinutesInput(e.target.value)}
                  className="w-28"
                  data-testid="input-inactivity-minutes"
                />
                <span className="text-sm text-muted-foreground">minutes of inactivity</span>
              </div>
              {showClampHint && (
                <p
                  className="text-sm text-amber-600 dark:text-amber-400"
                  data-testid="text-minutes-clamp-hint"
                >
                  Must be between 1 and 480 minutes — will be saved as {clampedMinutes}.
                </p>
              )}
              {showEmptyHint && (
                <p
                  className="text-sm text-amber-600 dark:text-amber-400"
                  data-testid="text-minutes-empty-hint"
                >
                  Enter a number of minutes (1–480) — will be saved as {clampedMinutes} if left empty.
                </p>
              )}
            </div>
          )}
        </div>

        <Button
          onClick={() => saveMutation.mutate()}
          disabled={saveMutation.isPending}
          data-testid="button-save-session-security"
        >
          <Save className="w-4 h-4 mr-2" />
          {saveMutation.isPending ? "Saving…" : "Save"}
        </Button>

        {/* Auto-login / credential caching disclosure */}
        <div className="rounded-lg border border-green-200 bg-green-50 dark:border-green-900 dark:bg-green-950/30 p-4 space-y-2">
          <div className="flex items-center gap-2 text-green-800 dark:text-green-300 font-medium text-sm">
            <Lock className="w-4 h-4" />
            No Auto-Login or Cached Credentials
          </div>
          <ul className="text-sm text-green-700 dark:text-green-400 space-y-1 list-disc list-inside">
            <li>Your password is never stored in your browser or local storage.</li>
            <li>Sessions use server-side storage only — no tokens are written to local storage or cookies on your device in a readable form.</li>
            <li>There is no "Remember me" feature. Every new browser session requires you to sign in.</li>
            <li>Your session stays alive as long as you keep using the app — the settings above are the only logout timer. Sessions left completely unused for 30 days are discarded server-side.</li>
            <li>Impersonation state and session keys are cleared on every login via session regeneration.</li>
          </ul>
        </div>
      </CardContent>
    </Card>
  );
}

// ── CSP Violations Section ────────────────────────────────────────────────────

interface CspViolationRow {
  id: number;
  blockedUri: string | null;
  violatedDirective: string | null;
  documentUri: string | null;
  createdAt: string;
}

function CspViolationsSection() {
  const [directiveFilter, setDirectiveFilter] = useState("");
  const [appliedFilter, setAppliedFilter] = useState("");

  const { data, isLoading, isError, refetch, isFetching } = useQuery<{
    violations: CspViolationRow[];
    total: number;
  }>({
    queryKey: ["/api/admin/csp-violations", { directive: appliedFilter }],
    queryFn: async () => {
      const params = new URLSearchParams({ limit: "100" });
      if (appliedFilter) params.set("directive", appliedFilter);
      const res = await fetch(`/api/admin/csp-violations?${params}`, {
        credentials: "include",
      });
      if (!res.ok) {
        const body = await res.json().catch(() => null);
        throw new Error(body?.error ?? `Failed to load CSP violations (${res.status})`);
      }
      return res.json();
    },
  });

  const violations = data?.violations ?? [];

  function handleFilter() {
    setAppliedFilter(directiveFilter.trim());
  }

  function handleClearFilter() {
    setDirectiveFilter("");
    setAppliedFilter("");
  }

  return (
    <div className="mt-8">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-xl font-bold flex items-center gap-2">
          <AlertTriangle className="w-5 h-5" />
          CSP Violations
        </h2>
        <Button variant="outline" size="sm" onClick={() => refetch()} disabled={isFetching}>
          <RefreshCw className="w-4 h-4 mr-2" />
          Refresh
        </Button>
      </div>

      <div className="flex gap-2 mb-4">
        <Input
          placeholder="Filter by violated directive (e.g. script-src)"
          value={directiveFilter}
          onChange={(e) => setDirectiveFilter(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && handleFilter()}
          className="max-w-xs"
          data-testid="input-csp-directive-filter"
        />
        <Button size="sm" onClick={handleFilter} data-testid="button-csp-apply-filter">
          Filter
        </Button>
        {appliedFilter && (
          <Button size="sm" variant="outline" onClick={handleClearFilter} data-testid="button-csp-clear-filter">
            Clear
          </Button>
        )}
      </div>

      {isLoading ? (
        <div className="space-y-2">
          {[1, 2, 3, 4, 5].map((i) => (
            <div key={i} className="h-10 bg-muted animate-pulse rounded" />
          ))}
        </div>
      ) : isError ? (
        <div className="text-center py-8 border rounded-lg" data-testid="error-csp-violations">
          <XCircle className="w-10 h-10 mx-auto mb-2 text-destructive opacity-70" />
          <p className="text-sm text-destructive mb-3">Failed to load CSP violations.</p>
          <Button variant="outline" size="sm" onClick={() => refetch()} data-testid="button-retry-csp-violations">
            <RefreshCw className="w-4 h-4 mr-2" />
            Retry
          </Button>
        </div>
      ) : violations.length === 0 ? (
        <div
          className="text-center py-8 text-muted-foreground border rounded-lg"
          data-testid="empty-csp-violations"
        >
          <CheckCircle2 className="w-10 h-10 mx-auto mb-2 opacity-30" />
          <p>No CSP violations recorded{appliedFilter ? " matching that directive" : ""}.</p>
        </div>
      ) : (
        <div className="border rounded-lg overflow-hidden">
          <table className="w-full text-sm" data-testid="table-csp-violations">
            <thead className="bg-muted/50">
              <tr>
                <th className="px-3 py-2 text-left font-medium text-muted-foreground">Violated Directive</th>
                <th className="px-3 py-2 text-left font-medium text-muted-foreground">Blocked URI</th>
                <th className="px-3 py-2 text-left font-medium text-muted-foreground hidden md:table-cell">
                  Document URI
                </th>
                <th className="px-3 py-2 text-left font-medium text-muted-foreground whitespace-nowrap">
                  Reported At
                </th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {violations.map((v) => (
                <tr key={v.id} className="hover:bg-muted/20">
                  <td className="px-3 py-2">
                    {v.violatedDirective ? (
                      <Badge variant="outline" className="font-mono text-xs">
                        {v.violatedDirective}
                      </Badge>
                    ) : (
                      <span className="text-muted-foreground">—</span>
                    )}
                  </td>
                  <td className="px-3 py-2 font-mono text-xs break-all max-w-xs">
                    {v.blockedUri || <span className="text-muted-foreground">—</span>}
                  </td>
                  <td className="px-3 py-2 text-muted-foreground text-xs break-all max-w-xs hidden md:table-cell">
                    {v.documentUri || "—"}
                  </td>
                  <td className="px-3 py-2 text-muted-foreground whitespace-nowrap">
                    {format(new Date(v.createdAt), "MMM d, HH:mm")}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="px-3 py-2 text-xs text-muted-foreground border-t bg-muted/20">
            Showing {violations.length} most recent violation{violations.length !== 1 ? "s" : ""}
            {appliedFilter ? ` matching "${appliedFilter}"` : ""}
          </div>
        </div>
      )}
    </div>
  );
}

function AdminPanelPageContent({ mode }: { mode: "settings" | "master" }) {
  const { isAdmin, isMasterAdmin, isLoading: authLoading, isAuthenticated, error: authError, user, tenant } = useAuth();
  const { toast } = useToast();
  const [showForm, setShowForm] = useState(false);
  const [editingCategory, setEditingCategory] = useState<Category | undefined>();
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [updateText, setUpdateText] = useState("");
  const [updateContentHtml, setUpdateContentHtml] = useState("");
  const [showUpdateForm, setShowUpdateForm] = useState(false);
  const [activeTab, setActiveTab] = useState("unread");
  const [isDownloadingBackup, setIsDownloadingBackup] = useState(false);
  const [backupFile, setBackupFile] = useState<File | null>(null);
  const [isDownloadingProductionBackup, setIsDownloadingProductionBackup] = useState(false);
  const [productionBackupFile, setProductionBackupFile] = useState<File | null>(null);
  const [jsonBackupFile, setJsonBackupFile] = useState<File | null>(null);
  const [jsonRestoreMode, setJsonRestoreMode] = useState<'full-replace' | 'merge'>('full-replace');
  const [jsonDryRunResult, setJsonDryRunResult] = useState<{ success: boolean; tablesRestored: string[]; recordCounts: Record<string, number>; errors: string[] } | null>(null);
  const [showAutomatedBackupDialog, setShowAutomatedBackupDialog] = useState(false);
  const [isRepairingData, setIsRepairingData] = useState(false);
  const [repairResult, setRepairResult] = useState<any>(null);
  const [automatedBackupFrequency, setAutomatedBackupFrequency] = useState<'daily' | 'weekly' | 'monthly'>('daily');
  const [showBackupTestDialog, setShowBackupTestDialog] = useState(false);
  const [backupTestResults, setBackupTestResults] = useState<any>(null);
  const { resetOrder} = useNavigationOrder([], isAuthenticated);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // Use auth data from useAuth hook (already handles 401s gracefully)
  // No need for duplicate /api/auth/user query - it can crash the component on session drops
  const authData = user && tenant ? { user: user as User, tenant: tenant as Tenant } : null;
  const authDataError = !!authError;

  // Fetch ALL categories (including goals-scoped) for the settings page
  const { data: allCategories = [], isLoading: categoriesLoading, isError: categoriesError } = useQuery<Category[]>({
    queryKey: ["/api/categories?scope=any"],
    queryFn: getQueryFn({ on401: "returnNull" }),
    enabled: isAuthenticated,
    retry: 2,
  });
  // Editable categories (all / sops scoped) — shown in sortable DnD list
  const categories = allCategories.filter(c => c.scope !== "goals");
  // Goals-only categories — shown read-only below
  const goalCategories = allCategories.filter(c => c.scope === "goals");

  // Fetch updates - only when authenticated
  const { data: updates = [], isError: updatesError } = useQuery<Update[]>({
    queryKey: ["/api/updates"],
    queryFn: getQueryFn({ on401: "returnNull" }),
    enabled: isAuthenticated,
    retry: 2,
  });

  // Fetch suggestions - only when authenticated and admin
  const { data: suggestions = [], isLoading: suggestionsLoading, isError: suggestionsError } = useQuery<AdminSuggestion[]>({
    queryKey: ["/api/admin/suggestions"],
    queryFn: getQueryFn({ on401: "returnNull" }),
    enabled: isAuthenticated && isAdmin,
    retry: 2,
  });

  const invalidateCategories = () => {
    queryClient.invalidateQueries({ queryKey: ["/api/categories"] });
    queryClient.invalidateQueries({ queryKey: ["/api/categories?scope=any"] });
    queryClient.invalidateQueries({ queryKey: ["/api/categories?includeGoalsOnly=true"] });
  };

  // Create category mutation
  const createCategoryMutation = useMutation({
    mutationFn: (data: NewCategory) => apiRequest("POST", "/api/categories", data),
    onSuccess: () => {
      invalidateCategories();
      setShowForm(false);
    },
    onError: () => {
    },
  });

  // Update category mutation
  const updateCategoryMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<Category> }) =>
      apiRequest("PATCH", `/api/categories/${id}`, data),
    onSuccess: () => {
      invalidateCategories();
      setEditingCategory(undefined);
    },
    onError: () => {
    },
  });

  // Delete category mutation
  const deleteCategoryMutation = useMutation({
    mutationFn: (id: string) => apiRequest("DELETE", `/api/categories/${id}`),
    onSuccess: () => {
      invalidateCategories();
    },
    onError: () => {
    },
  });

  // Reorder categories mutation
  const reorderCategoriesMutation = useMutation({
    mutationFn: (orderedIds: string[]) =>
      apiRequest("POST", "/api/categories/reorder", { orderedIds }),
    onSuccess: () => {
      invalidateCategories();
    },
    onError: () => {
    },
  });

  // Change password mutation
  const changePasswordMutation = useMutation({
    mutationFn: (data: { currentPassword: string; newPassword: string }) =>
      apiRequest("POST", "/api/auth/change-password", data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/auth/user"] });
      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");
    },
    onError: (error: any) => {
    },
  });

  // Helper to extract plain text from HTML
  const extractPlainText = (html: string): string => {
    const doc = new DOMParser().parseFromString(html, 'text/html');
    return doc.body.textContent || '';
  };

  // Create update mutation
  const createUpdateMutation = useMutation({
    mutationFn: (data: { text: string; contentHtml: string }) => 
      apiRequest("POST", "/api/updates", data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/updates"] });
      setUpdateText("");
      setUpdateContentHtml("");
      setShowUpdateForm(false);
    },
    onError: () => {
    },
  });

  // Delete update mutation
  const deleteUpdateMutation = useMutation({
    mutationFn: (id: string) => apiRequest("DELETE", `/api/updates/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/updates"] });
    },
    onError: () => {
    },
  });

  // Backup download handler
  const handleDownloadBackup = async () => {
    setIsDownloadingBackup(true);
    try {
      const response = await fetch('/api/backup/download', {
        method: 'GET',
        credentials: 'include',
      });
      
      if (!response.ok) {
        throw new Error('Failed to download backup');
      }
      
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `goalvia-backup-${new Date().toISOString().split('T')[0]}.zip`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      
      toast({
        title: "Backup Downloaded",
        description: "Your data has been backed up successfully",
      });
    } catch (error) {
      console.error('Backup download error:', error);
      toast({
        title: "Backup Failed",
        description: "Failed to download backup. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsDownloadingBackup(false);
    }
  };

  // Restore mutation
  const restoreBackupMutation = useMutation({
    mutationFn: async (file: File) => {
      const formData = new FormData();
      formData.append('backupFile', file);
      
      const response = await fetch('/api/backup/restore', {
        method: 'POST',
        credentials: 'include',
        body: formData,
      });
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.details || error.error || 'Failed to restore backup');
      }
      
      return response.json();
    },
    onSuccess: () => {
      toast({
        title: "Backup Restored",
        description: "Your data has been restored successfully. Refreshing...",
      });
      // Invalidate all queries to refresh data
      queryClient.invalidateQueries();
      // Refresh the page after a short delay
      setTimeout(() => window.location.reload(), 1500);
    },
    onError: (error: Error) => {
      console.error('Restore error:', error);
      toast({
        title: "Restore Failed",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const handleRestoreBackup = () => {
    if (!backupFile) return;
    
    if (!confirm('⚠️ WARNING: Restoring a backup will PERMANENTLY DELETE all your current data and replace it with the backup data. This action cannot be undone. Are you absolutely sure you want to continue?')) {
      return;
    }
    
    restoreBackupMutation.mutate(backupFile);
  };

  // Production backup download handler
  const handleDownloadProductionBackup = async () => {
    setIsDownloadingProductionBackup(true);
    try {
      const response = await fetch('/api/admin/production-backup/download', {
        method: 'GET',
        credentials: 'include',
      });
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to download production backup');
      }
      
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `production-backup-${new Date().toISOString().split('T')[0]}.zip`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      
      toast({
        title: "Production Backup Downloaded",
        description: "Entire database has been backed up successfully",
      });
    } catch (error: any) {
      console.error('Production backup download error:', error);
      toast({
        title: "Production Backup Failed",
        description: error.message || "Failed to download production backup. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsDownloadingProductionBackup(false);
    }
  };

  // Production restore mutation
  const restoreProductionBackupMutation = useMutation({
    mutationFn: async (file: File) => {
      const formData = new FormData();
      formData.append('backupFile', file);
      
      const response = await fetch('/api/admin/production-backup/restore', {
        method: 'POST',
        credentials: 'include',
        body: formData,
      });
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.details || error.error || 'Failed to restore production backup');
      }
      
      return response.json();
    },
    onSuccess: () => {
      toast({
        title: "Production Database Restored",
        description: "Entire database has been restored successfully. Refreshing...",
      });
      // Invalidate all queries to refresh data
      queryClient.invalidateQueries();
      // Refresh the page after a short delay
      setTimeout(() => window.location.reload(), 2000);
    },
    onError: (error: Error) => {
      console.error('Production restore error:', error);
      toast({
        title: "Production Restore Failed",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const handleRestoreProductionBackup = () => {
    if (!productionBackupFile) return;
    
    if (!confirm('🚨 CRITICAL WARNING: You are about to RESTORE THE ENTIRE PRODUCTION DATABASE. This will PERMANENTLY DELETE ALL DATA from ALL USERS and ALL TENANTS and replace it with the backup data. This action is IRREVERSIBLE and will affect everyone using the system. Type "RESTORE PRODUCTION" in the next dialog to confirm.')) {
      return;
    }
    
    const confirmation = prompt('Type exactly "RESTORE PRODUCTION" to confirm this critical operation:');
    if (confirmation !== 'RESTORE PRODUCTION') {
      toast({
        title: "Restore Cancelled",
        description: "Production restore was cancelled for safety",
      });
      return;
    }
    
    restoreProductionBackupMutation.mutate(productionBackupFile);
  };

  // JSON restore mutation
  const restoreJsonBackupMutation = useMutation({
    mutationFn: async ({ backupData, dryRun, mode }: { backupData: any; dryRun: boolean; mode: 'full-replace' | 'merge' }) => {
      const response = await fetch('/api/admin/production-backup/restore-json', {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ backupData, dryRun, mode }),
      });
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.details || error.error || 'Failed to restore from JSON backup');
      }
      
      return response.json();
    },
    onSuccess: (result, variables) => {
      if (variables.dryRun) {
        setJsonDryRunResult(result);
        toast({
          title: "Dry Run Complete",
          description: `Preview: ${result.tablesRestored.length} tables would be restored with ${Object.values(result.recordCounts as Record<string, number>).reduce((a, b) => a + b, 0)} total records`,
        });
      } else {
        toast({
          title: "JSON Database Restored",
          description: `Restored ${result.tablesRestored.length} tables successfully. Refreshing...`,
        });
        // Invalidate all queries to refresh data
        queryClient.invalidateQueries();
        // Refresh the page after a short delay
        setTimeout(() => window.location.reload(), 2000);
      }
    },
    onError: (error: Error) => {
      console.error('JSON restore error:', error);
      toast({
        title: "JSON Restore Failed",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const handleJsonDryRun = async () => {
    if (!jsonBackupFile) return;
    
    try {
      const text = await jsonBackupFile.text();
      const backupData = JSON.parse(text);
      restoreJsonBackupMutation.mutate({ backupData, dryRun: true, mode: jsonRestoreMode });
    } catch (error: any) {
      toast({
        title: "Invalid JSON File",
        description: "The selected file is not a valid JSON backup file",
        variant: "destructive",
      });
    }
  };

  const handleRestoreJsonBackup = async () => {
    if (!jsonBackupFile) return;
    
    if (!confirm('🚨 CRITICAL WARNING: You are about to RESTORE THE ENTIRE PRODUCTION DATABASE FROM JSON. This will PERMANENTLY DELETE ALL DATA from ALL USERS and ALL TENANTS and replace it with the backup data. This action is IRREVERSIBLE. Type "RESTORE JSON" in the next dialog to confirm.')) {
      return;
    }
    
    const confirmation = prompt('Type exactly "RESTORE JSON" to confirm this critical operation:');
    if (confirmation !== 'RESTORE JSON') {
      toast({
        title: "Restore Cancelled",
        description: "JSON restore was cancelled for safety",
      });
      return;
    }
    
    try {
      const text = await jsonBackupFile.text();
      const backupData = JSON.parse(text);
      restoreJsonBackupMutation.mutate({ backupData, dryRun: false, mode: jsonRestoreMode });
    } catch (error: any) {
      toast({
        title: "Invalid JSON File",
        description: "The selected file is not a valid JSON backup file",
        variant: "destructive",
      });
    }
  };

  // Data repair handler
  const handleDataRepair = async () => {
    setIsRepairingData(true);
    setRepairResult(null);
    try {
      const response = await fetch('/api/admin/tenant-repair', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ dryRun: false }),
      });
      const result = await response.json();
      setRepairResult(result);
      if (response.ok) {
        toast({
          title: "Data Repair Complete",
          description: `Fixed ${result.repairs?.length || 0} table(s) with mismatched user IDs`,
        });
        // Invalidate all queries to refresh data
        queryClient.invalidateQueries();
      } else {
        toast({
          title: "Repair Failed",
          description: result.error || "Failed to repair data",
          variant: "destructive",
        });
      }
    } catch (error: any) {
      toast({
        title: "Repair Failed",
        description: error.message || "Failed to repair data",
        variant: "destructive",
      });
    } finally {
      setIsRepairingData(false);
    }
  };

  // Query for user preferences (includes automated backup settings)
  const { data: userPreferences } = useQuery<any>({
    queryKey: ["/api/user/preferences"],
    queryFn: getQueryFn({ on401: "returnNull" }),
    enabled: isAuthenticated,
  });

  // Query for Drive connection status
  const { data: driveConnection } = useQuery<any>({
    queryKey: ["/api/google-drive/connection"],
    queryFn: getQueryFn({ on401: "returnNull" }),
    enabled: isAuthenticated,
  });

  // Setup automated backup mutation
  const setupAutomatedBackupMutation = useMutation({
    mutationFn: async (frequency: 'daily' | 'weekly' | 'monthly') => {
      return apiRequest("POST", "/api/backup/automated/setup", { frequency });
    },
    onSuccess: () => {
      toast({
        title: "Automated Backup Enabled",
        description: "Your backups will now be automatically uploaded to Google Drive",
      });
      queryClient.invalidateQueries({ queryKey: ["/api/user/preferences"] });
      setShowAutomatedBackupDialog(false);
    },
    onError: (error: any) => {
      toast({
        title: "Setup Failed",
        description: error.message || "Failed to setup automated backup",
        variant: "destructive",
      });
    },
  });

  // Disable automated backup mutation
  const disableAutomatedBackupMutation = useMutation({
    mutationFn: async () => {
      return apiRequest("POST", "/api/backup/automated/disable", {});
    },
    onSuccess: () => {
      toast({
        title: "Automated Backup Disabled",
        description: "Automatic backups have been turned off",
      });
      queryClient.invalidateQueries({ queryKey: ["/api/user/preferences"] });
    },
    onError: (error: any) => {
      toast({
        title: "Failed to Disable",
        description: error.message || "Failed to disable automated backup",
        variant: "destructive",
      });
    },
  });

  // Run backup now mutation
  const runBackupNowMutation = useMutation({
    mutationFn: async () => {
      const res = await apiRequest("POST", "/api/backup/automated/run-now", {});
      return res.json();
    },
    onSuccess: (data: any) => {
      toast({
        title: "Backup Completed",
        description: data?.fileName
          ? `Backup file ${data.fileName} uploaded to Google Drive successfully`
          : "Backup uploaded to Google Drive successfully",
      });
      queryClient.invalidateQueries({ queryKey: ["/api/user/preferences"] });
    },
    onError: (error: any) => {
      toast({
        title: "Backup Failed",
        description: error.message || "Failed to run backup",
        variant: "destructive",
      });
    },
  });

  const hiddenMenuItems: string[] = (userPreferences?.hiddenMenuItems as string[]) || [];

  const toggleMenuItemMutation = useMutation({
    mutationFn: async (itemId: string) => {
      const current = (userPreferences?.hiddenMenuItems as string[]) || [];
      const updated = current.includes(itemId)
        ? current.filter((id: string) => id !== itemId)
        : [...current, itemId];
      return apiRequest("PATCH", "/api/user/preferences", { hiddenMenuItems: updated });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/user/preferences"] });
    },
    onError: () => {
      toast({ title: "Failed to update menu visibility", variant: "destructive" });
    },
  });

  const menuItems = menuItemDefinitions.map(item => ({
    id: item.id,
    name: item.name,
    icon: item.icon,
    ...(item.isSection ? { isSection: true } : {}),
    ...(item.parentId ? { parent: item.parentId } : {}),
  }));

  // Test backup connection mutation
  const testBackupConnectionMutation = useMutation({
    mutationFn: async () => {
      return apiRequest("POST", "/api/backup/test-drive-connection", {});
    },
    onSuccess: (data: any) => {
      setBackupTestResults(data);
      setShowBackupTestDialog(true);
    },
    onError: (error: any) => {
      toast({
        title: "Test Failed",
        description: error.message || "Failed to test backup connection",
        variant: "destructive",
      });
    },
  });

  const handleSetupAutomatedBackup = () => {
    // Check if Drive is connected
    if (!driveConnection?.connected) {
      toast({
        title: "Google Drive Not Connected",
        description: "Please connect Google Drive first to use automated backups",
        variant: "destructive",
      });
      return;
    }
    
    setShowAutomatedBackupDialog(true);
  };

  const handleConfirmAutomatedBackup = () => {
    setupAutomatedBackupMutation.mutate(automatedBackupFrequency);
  };

  const handleDisableAutomatedBackup = () => {
    if (confirm('Are you sure you want to disable automated backups?')) {
      disableAutomatedBackupMutation.mutate();
    }
  };

  // Update suggestion status mutation
  const updateStatusMutation = useMutation({
    mutationFn: ({ id, status }: { id: string; status: string }) =>
      apiRequest("PATCH", `/api/admin/suggestions/${id}/status`, { status }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/suggestions"] });
    },
    onError: () => {
    },
  });

  const handleSaveCategory = (data: NewCategory) => {
    if (editingCategory) {
      updateCategoryMutation.mutate({
        id: editingCategory.id,
        data,
      });
    } else {
      createCategoryMutation.mutate(data);
    }
  };

  const handleEditCategory = (category: Category) => {
    setEditingCategory(category);
    setShowForm(true);
  };

  const handleDeleteCategory = (id: string) => {
    if (confirm("Are you sure you want to delete this category?")) {
      deleteCategoryMutation.mutate(id);
    }
  };

  const handleCancel = () => {
    setShowForm(false);
    setEditingCategory(undefined);
  };

  const handleChangePassword = (e: React.FormEvent) => {
    e.preventDefault();

    if (newPassword.length < 8) {
      return;
    }

    if (newPassword !== confirmPassword) {
      return;
    }

    changePasswordMutation.mutate({
      currentPassword,
      newPassword,
    });
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      const oldIndex = categories.findIndex((cat) => cat.id === active.id);
      const newIndex = categories.findIndex((cat) => cat.id === over.id);

      const newOrder = arrayMove(categories, oldIndex, newIndex);
      const orderedIds = newOrder.map((cat) => cat.id);
      
      reorderCategoriesMutation.mutate(orderedIds);
    }
  };

  const filteredSuggestions = suggestions.filter((s) => {
    if (activeTab === "all") return true;
    return s.status === activeTab;
  });

  const statusCounts = {
    all: suggestions.length,
    unread: suggestions.filter((s) => s.status === "unread").length,
    done: suggestions.filter((s) => s.status === "done").length,
    deferred: suggestions.filter((s) => s.status === "deferred").length,
    archived: suggestions.filter((s) => s.status === "archived").length,
  };

  // Show loading state while auth is being checked
  if (authLoading) {
    return (
      <div className="p-6 space-y-6">
        <div className="mb-6">
          <Skeleton className="h-10 w-64 mb-2" />
          <Skeleton className="h-4 w-96" />
        </div>
        <div className="space-y-4">
          {[1, 2, 3].map((i) => (
            <Card key={i}>
              <CardHeader>
                <Skeleton className="h-4 w-full" />
              </CardHeader>
            </Card>
          ))}
        </div>
      </div>
    );
  }

  // Show login prompt if not authenticated
  if (!isAuthenticated) {
    return (
      <div className="p-6 space-y-6">
        <div className="flex flex-col items-center justify-center min-h-[400px] text-center">
          <Shield className="w-16 h-16 text-muted-foreground mb-4" />
          <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-2">
            Authentication Required
          </h1>
          <p className="text-gray-600 dark:text-gray-400 mb-6 max-w-md">
            Please log in to access the admin panel and manage your account settings.
          </p>
          <Button asChild>
            <a href="/api/login">Log In</a>
          </Button>
        </div>
      </div>
    );
  }

  // Master Admin page is only available to the platform owner
  if (mode === "master" && !isMasterAdmin) {
    return (
      <div className="p-6 space-y-6">
        <div className="flex flex-col items-center justify-center min-h-[400px] text-center">
          <Shield className="w-16 h-16 text-muted-foreground mb-4" />
          <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-2" data-testid="heading-master-admin-denied">
            Access Restricted
          </h1>
          <p className="text-gray-600 dark:text-gray-400 mb-6 max-w-md">
            The Master Admin area is only available to the platform owner.
          </p>
          <Link href="/settings">
            <Button data-testid="button-go-to-settings">Go to Settings</Button>
          </Link>
        </div>
      </div>
    );
  }

  // Show error state if data failed to load
  if (categoriesError || authDataError) {
    return (
      <div className="p-6 space-y-6">
        <div className="flex flex-col items-center justify-center min-h-[400px] text-center">
          <XCircle className="w-16 h-16 text-destructive mb-4" />
          <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-2">
            Failed to Load Settings
          </h1>
          <p className="text-gray-600 dark:text-gray-400 mb-6 max-w-md">
            There was an error loading your settings. Please try refreshing the page.
          </p>
          <Button onClick={() => window.location.reload()}>
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh Page
          </Button>
        </div>
      </div>
    );
  }

  if (categoriesLoading) {
    return (
      <div className="p-6 space-y-6">
        <div className="mb-6">
          <Skeleton className="h-10 w-64 mb-2" />
          <Skeleton className="h-4 w-96" />
        </div>
        <div className="space-y-4">
          {[1, 2, 3, 4, 5].map((i) => (
            <Card key={i}>
              <CardHeader>
                <Skeleton className="h-4 w-full" />
                <Skeleton className="h-4 w-3/4" />
              </CardHeader>
              <CardContent>
                <Skeleton className="h-4 w-32" />
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Link href="/">
            <Button variant="ghost" size="icon" className="h-10 w-10" data-testid="btn-back-to-dashboard">
              <ArrowLeft className="h-5 w-5" />
            </Button>
          </Link>
          <div>
            <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100" data-testid="heading-admin-panel">
              {mode === "master" ? "Master Admin" : "Settings"}
            </h1>
            <p className="text-gray-600 dark:text-gray-400 mt-1">
              {mode === "master"
                ? "Platform-level administration for the platform owner"
                : "Manage your account, preferences, and system settings"}
            </p>
          </div>
        </div>
      </div>

      {mode === "settings" && (<>
      {/* Account Information Section */}
      <Card>
        <CardHeader>
          <CardTitle>Account Information</CardTitle>
          <CardDescription>Your personal account details</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label className="text-sm font-medium text-muted-foreground">Name</Label>
              <p className="text-base mt-1" data-testid="text-user-name">
                {authData?.user?.firstName} {authData?.user?.lastName}
              </p>
            </div>
            <div>
              <Label className="text-sm font-medium text-muted-foreground">Email</Label>
              <p className="text-base mt-1" data-testid="text-user-email">
                {authData?.user?.email}
              </p>
            </div>
            <div>
              <Label className="text-sm font-medium text-muted-foreground">Username</Label>
              <p className="text-base mt-1" data-testid="text-user-username">
                {authData?.user?.username}
              </p>
            </div>
            <div>
              <Label className="text-sm font-medium text-muted-foreground">Role</Label>
              <p className="text-base mt-1" data-testid="text-user-role">
                {authData?.user?.role}
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Change Password Section */}
      <Card>
        <CardHeader>
          <CardTitle>Change Password</CardTitle>
          <CardDescription>Update your password to keep your account secure</CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleChangePassword} className="space-y-4">
            <div>
              <Label htmlFor="current-password">Current Password</Label>
              <Input
                id="current-password"
                type="password"
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                placeholder="Enter your current password"
                required
                data-testid="input-current-password"
              />
            </div>

            <div>
              <Label htmlFor="new-password">New Password</Label>
              <Input
                id="new-password"
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="Enter new password (min 8 characters)"
                required
                minLength={8}
                data-testid="input-new-password"
              />
            </div>

            <div>
              <Label htmlFor="confirm-password">Confirm New Password</Label>
              <Input
                id="confirm-password"
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="Confirm new password"
                required
                data-testid="input-confirm-password"
              />
            </div>

            <Button
              type="submit"
              disabled={changePasswordMutation.isPending}
              data-testid="button-change-password"
            >
              <Save className="w-4 h-4 mr-2" />
              {changePasswordMutation.isPending ? "Changing..." : "Change Password"}
            </Button>
          </form>
        </CardContent>
      </Card>

      {/* User Preferences Section */}
      <Card>
        <CardHeader>
          <CardTitle>User Preferences</CardTitle>
          <CardDescription>Manage your application preferences and settings</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div className="flex items-center justify-between p-4 border rounded-lg">
              <div>
                <h3 className="font-medium">Navigation Menu Order</h3>
                <p className="text-sm text-muted-foreground">
                  Reset the sidebar navigation menu to default order. This will restore all menu items including Notepad, Tasks, Goals, etc.
                </p>
              </div>
              <Button
                variant="outline"
                onClick={() => {
                  resetOrder();
                }}
                data-testid="button-reset-navigation"
              >
                <RefreshCw className="w-4 h-4 mr-2" />
                Reset Navigation
              </Button>
            </div>
            
            <div className="flex items-center justify-between p-4 border rounded-lg">
              <div>
                <h3 className="font-medium">Notification Settings</h3>
                <p className="text-sm text-muted-foreground">
                  Configure email, SMS, and in-app notifications. Set up event reminders and daily summaries.
                </p>
              </div>
              <Link href="/notification-settings">
                <Button
                  variant="outline"
                  data-testid="button-notification-settings"
                >
                  <Bell className="w-4 h-4 mr-2" />
                  Configure Notifications
                </Button>
              </Link>
            </div>
            
            <div className="flex items-center justify-between p-4 border rounded-lg">
              <div>
                <h3 className="font-medium">Assistants</h3>
                <p className="text-sm text-muted-foreground">
                  Invite virtual assistants and share only the individual records they need.
                </p>
              </div>
              <Link href="/assistants">
                <Button
                  variant="outline"
                  data-testid="button-assistants"
                >
                  <Users className="w-4 h-4 mr-2" />
                  Manage Assistants
                </Button>
              </Link>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Session Security Section */}
      <SessionSecurityCard userPreferences={userPreferences} />

      {/* Custom Menu Section */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Menu className="w-5 h-5" />
            Custom Menu
          </CardTitle>
          <CardDescription>Toggle sidebar menu items on or off. Hidden items are only hidden from view, never removed.</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-1">
            {menuItems.map((item) => {
              const ItemIcon = item.icon;
              const isVisible = !hiddenMenuItems.includes(item.id);
              const isSection = 'isSection' in item && item.isSection;
              const hasParent = 'parent' in item && item.parent;

              return (
                <div
                  key={item.id}
                  className={`flex items-center justify-between p-3 rounded-lg hover:bg-muted/50 transition-colors ${
                    hasParent ? 'ml-6' : ''
                  } ${isSection ? 'mt-3 first:mt-0' : ''}`}
                  data-testid={`menu-toggle-${item.id}`}
                >
                  <div className="flex items-center gap-3">
                    <ItemIcon className={`w-4 h-4 ${isVisible ? 'text-foreground' : 'text-muted-foreground'}`} />
                    <span className={`text-sm font-medium ${isVisible ? 'text-foreground' : 'text-muted-foreground line-through'}`}>
                      {item.name}
                    </span>
                    {isSection && (
                      <Badge variant="outline" className="text-xs">Section</Badge>
                    )}
                  </div>
                  <Switch
                    checked={isVisible}
                    onCheckedChange={() => toggleMenuItemMutation.mutate(item.id)}
                    disabled={toggleMenuItemMutation.isPending}
                    data-testid={`switch-menu-${item.id}`}
                  />
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Category Management Section */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>Categories</CardTitle>
              <CardDescription>
                Organize your goals and habits with custom categories. Drag to reorder.
              </CardDescription>
            </div>
            {!showForm && (
              <Button onClick={() => setShowForm(true)} data-testid="button-add-category">
                <Plus className="w-4 h-4 mr-2" />
                Add Category
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent>
          {showForm && (
            <CategoryForm
              category={editingCategory}
              onSave={handleSaveCategory}
              onCancel={handleCancel}
            />
          )}

          {categories.length === 0 && goalCategories.length === 0 ? (
            <div className="text-center py-8 text-gray-500 dark:text-gray-400">
              No categories found. Add your first category to get started.
            </div>
          ) : (
            <div className="space-y-6">
              {/* Editable categories — sortable */}
              {categories.length > 0 && (
                <DndContext
                  sensors={sensors}
                  collisionDetection={closestCenter}
                  onDragEnd={handleDragEnd}
                >
                  <SortableContext
                    items={categories.map((cat) => cat.id)}
                    strategy={verticalListSortingStrategy}
                  >
                    <div className="space-y-2" data-testid="categories-list">
                      {categories.map((category) => (
                        <SortableCategoryItem
                          key={category.id}
                          category={category}
                          onEdit={handleEditCategory}
                          onDelete={handleDeleteCategory}
                        />
                      ))}
                    </div>
                  </SortableContext>
                </DndContext>
              )}

              {/* Goals-only categories — read-only */}
              {goalCategories.length > 0 && (
                <div>
                  <div className="flex items-center gap-2 mb-3">
                    <h4 className="text-sm font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wide">
                      Goals Categories
                    </h4>
                    <div className="flex-1 h-px bg-blue-100 dark:bg-blue-900/30" />
                    <span className="text-xs text-gray-400 dark:text-gray-500 flex items-center gap-1">
                      <Lock className="w-3 h-3" /> Read-only
                    </span>
                  </div>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mb-3">
                    These categories are available in Goals and can now also be used in Habits and TrackerX. They cannot be edited or deleted.
                  </p>
                  <div className="space-y-2" data-testid="goals-categories-list">
                    {goalCategories.map((category) => (
                      <GoalCategoryItem key={category.id} category={category} />
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>
      </>)}

      {/* Admin Links Section */}
      {mode === "master" && (isMasterAdmin || authData?.user?.role === 'admin') && (
        <Card>
          <CardHeader>
            <div className="flex items-center gap-2">
              <Shield className="w-5 h-5 text-purple-600" />
              <div>
                <CardTitle>Admin Tools</CardTitle>
                <CardDescription>
                  Manage team members and organization settings
                </CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="flex items-center justify-between p-4 border rounded-lg">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-purple-600 rounded-lg flex items-center justify-center">
                    <Users className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <h3 className="font-semibold">User Management</h3>
                    <p className="text-sm text-muted-foreground">
                      Invite team members and manage user roles
                    </p>
                  </div>
                </div>
                <Link href="/user-management">
                  <Button data-testid="button-user-management">
                    <Users className="w-4 h-4 mr-2" />
                    Manage Users
                  </Button>
                </Link>
              </div>

              <div className="flex items-center justify-between p-4 border rounded-lg">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-gradient-to-br from-green-500 to-blue-600 rounded-lg flex items-center justify-center">
                    <Store className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <h3 className="font-semibold">Sales & Storefront</h3>
                    <p className="text-sm text-muted-foreground">
                      Manage pricing plans, affiliates, coupons, and branding
                    </p>
                  </div>
                </div>
                <Link href="/reseller">
                  <Button data-testid="button-reseller">
                    <Store className="w-4 h-4 mr-2" />
                    Configure Plans
                  </Button>
                </Link>
              </div>

              <div className="flex items-center justify-between p-4 border rounded-lg">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-gradient-to-br from-purple-500 to-pink-600 rounded-lg flex items-center justify-center">
                    <Layout className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <h3 className="font-semibold">Landing Page Builder</h3>
                    <p className="text-sm text-muted-foreground">
                      Create landing pages with drag-and-drop builder and A/B testing
                    </p>
                  </div>
                </div>
                <Link href="/landing-page-builder">
                  <Button data-testid="button-landing-page-builder">
                    <Layout className="w-4 h-4 mr-2" />
                    Build Pages
                  </Button>
                </Link>
              </div>

              <div className="flex items-center justify-between p-4 border rounded-lg">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-lg flex items-center justify-center">
                    <Building className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <h3 className="font-semibold">Manage Tenants</h3>
                    <p className="text-sm text-muted-foreground">
                      View and manage all tenants in the system
                    </p>
                  </div>
                </div>
                <Link href="/admin/tenants">
                  <Button data-testid="button-manage-tenants">
                    <Building className="w-4 h-4 mr-2" />
                    View Tenants
                  </Button>
                </Link>
              </div>

              <div className="flex items-center justify-between p-4 border rounded-lg">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-gradient-to-br from-cyan-500 to-blue-600 rounded-lg flex items-center justify-center">
                    <RefreshCw className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <h3 className="font-semibold">Tenant Migration</h3>
                    <p className="text-sm text-muted-foreground">
                      Migrate users to isolated tenants for data separation
                    </p>
                  </div>
                </div>
                <Link href="/admin/migration">
                  <Button data-testid="button-tenant-migration">
                    <RefreshCw className="w-4 h-4 mr-2" />
                    Manage Migration
                  </Button>
                </Link>
              </div>

              <div className="flex items-center justify-between p-4 border rounded-lg">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-gradient-to-br from-emerald-500 to-green-600 rounded-lg flex items-center justify-center">
                    <DollarSign className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <h3 className="font-semibold">Platform Billing</h3>
                    <p className="text-sm text-muted-foreground">AI & storage upcharge multipliers, usage summary, bill tenants</p>
                  </div>
                </div>
                <Link href="/admin/platform-billing">
                  <Button variant="outline">
                    <DollarSign className="w-4 h-4 mr-2" />
                    Manage Billing
                  </Button>
                </Link>
              </div>

              {(isAdmin || isMasterAdmin) && (
                <div className="flex items-center justify-between p-4 border rounded-lg">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-gradient-to-br from-indigo-500 to-indigo-700 rounded-lg flex items-center justify-center">
                      <BookUser className="w-5 h-5 text-white" />
                    </div>
                    <div>
                      <h3 className="font-semibold">Manage SOPs</h3>
                      <p className="text-sm text-muted-foreground">
                        Document platform procedures for human and AI assistants
                      </p>
                    </div>
                  </div>
                  <Link href="/admin/sops">
                    <Button data-testid="button-manage-sops">
                      <BookUser className="w-4 h-4 mr-2" />
                      Manage SOPs
                    </Button>
                  </Link>
                </div>
              )}

              {isAdmin && (
                <div className="flex items-center justify-between p-4 border rounded-lg">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-gradient-to-br from-amber-500 to-orange-600 rounded-lg flex items-center justify-center">
                      <Lightbulb className="w-5 h-5 text-white" />
                    </div>
                    <div>
                      <h3 className="font-semibold">Manage Suggestions</h3>
                      <p className="text-sm text-muted-foreground">
                        Review and manage community feature requests
                      </p>
                    </div>
                  </div>
                  <Link href="/admin/suggestions">
                    <Button data-testid="button-manage-suggestions">
                      <Lightbulb className="w-4 h-4 mr-2" />
                      View Suggestions
                    </Button>
                  </Link>
                </div>
              )}

              {isMasterAdmin && (
                <div className="flex items-center justify-between p-4 border rounded-lg">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-gradient-to-br from-pink-500 to-rose-600 rounded-lg flex items-center justify-center">
                      <Smile className="w-5 h-5 text-white" />
                    </div>
                    <div>
                      <h3 className="font-semibold">Manage Testimonials</h3>
                      <p className="text-sm text-muted-foreground">
                        Review and approve user testimonials
                      </p>
                    </div>
                  </div>
                  <Link href="/admin/testimonials">
                    <Button data-testid="button-manage-testimonials">
                      <Smile className="w-4 h-4 mr-2" />
                      View Testimonials
                    </Button>
                  </Link>
                </div>
              )}

              {isMasterAdmin && (
                <div className="p-4 border rounded-lg space-y-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-lg flex items-center justify-center">
                        <Database className="w-5 h-5 text-white" />
                      </div>
                      <div>
                        <h3 className="font-semibold">Production Database Backup & Restore</h3>
                        <p className="text-sm text-muted-foreground">
                          Backup or restore the entire production database
                        </p>
                      </div>
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="p-3 border rounded-lg bg-blue-500/5 border-blue-500/30">
                      <h4 className="text-sm font-medium text-blue-900 dark:text-blue-100 mb-2 flex items-center gap-2">
                        <Download className="w-4 h-4" />
                        Download Backup
                      </h4>
                      <div className="flex gap-2">
                        <a href="/api/admin/production-backup/download" download className="flex-1">
                          <Button variant="outline" size="sm" className="w-full" data-testid="button-backup-pgdump">
                            SQL Backup
                          </Button>
                        </a>
                        <a href="/api/admin/production-backup/json" download className="flex-1">
                          <Button size="sm" className="w-full" data-testid="button-backup-json">
                            JSON Backup
                          </Button>
                        </a>
                      </div>
                    </div>
                    
                    <div className="p-3 border rounded-lg bg-red-500/5 border-red-500/30">
                      <h4 className="text-sm font-medium text-red-900 dark:text-red-100 mb-2 flex items-center gap-2">
                        <Upload className="w-4 h-4" />
                        Restore Database
                      </h4>
                      <div className="flex gap-2">
                        <Button 
                          variant="outline" 
                          size="sm" 
                          className="flex-1 border-red-300 text-red-700 hover:bg-red-50 dark:border-red-700 dark:text-red-300 dark:hover:bg-red-900/20"
                          onClick={() => {
                            document.getElementById('production-backup-section')?.scrollIntoView({ behavior: 'smooth' });
                          }}
                          data-testid="button-goto-sql-restore"
                        >
                          SQL Restore
                        </Button>
                        <Button 
                          variant="outline" 
                          size="sm" 
                          className="flex-1 border-amber-300 text-amber-700 hover:bg-amber-50 dark:border-amber-700 dark:text-amber-300 dark:hover:bg-amber-900/20"
                          onClick={() => {
                            document.getElementById('production-backup-section')?.scrollIntoView({ behavior: 'smooth' });
                          }}
                          data-testid="button-goto-json-restore"
                        >
                          JSON Restore
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Data Repair Section - Master Admin Only */}
              {isMasterAdmin && (
                <div className="p-4 border rounded-lg border-purple-500/30 bg-purple-500/5">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-gradient-to-br from-purple-500 to-violet-600 rounded-lg flex items-center justify-center">
                        <RefreshCw className="w-5 h-5 text-white" />
                      </div>
                      <div>
                        <h3 className="font-semibold">Data Repair Tool</h3>
                        <p className="text-sm text-muted-foreground">
                          Fix user/tenant ID mismatches in media files, goals, and progress entries
                        </p>
                      </div>
                    </div>
                    <Button 
                      onClick={handleDataRepair}
                      disabled={isRepairingData}
                      className="bg-purple-600 hover:bg-purple-700"
                      data-testid="button-repair-data"
                    >
                      <RefreshCw className={`w-4 h-4 mr-2 ${isRepairingData ? 'animate-spin' : ''}`} />
                      {isRepairingData ? "Repairing..." : "Repair Data"}
                    </Button>
                  </div>
                  {repairResult && (
                    <div className="mt-4 p-3 bg-muted rounded-lg">
                      <h4 className="font-medium mb-2">Repair Results:</h4>
                      <pre className="text-xs overflow-auto max-h-40">{JSON.stringify(repairResult, null, 2)}</pre>
                    </div>
                  )}
                </div>
              )}

              {/* Updates Management Section */}
              <div className="p-4 border rounded-lg">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 bg-gradient-to-br from-orange-500 to-red-600 rounded-lg flex items-center justify-center">
                    <Megaphone className="w-5 h-5 text-white" />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-semibold">Updates & Announcements</h3>
                    <p className="text-sm text-muted-foreground">
                      Post updates visible to all users via the dashboard megaphone icon
                    </p>
                  </div>
                  {!showUpdateForm && (
                    <Button
                      onClick={() => setShowUpdateForm(true)}
                      size="sm"
                      data-testid="button-create-update"
                    >
                      <Plus className="w-4 h-4 mr-2" />
                      New Update
                    </Button>
                  )}
                </div>

                {showUpdateForm && (
                  <div className="mb-4 p-4 border rounded-lg bg-muted/50">
                    <Label>Announcement Content</Label>
                    <div className="mt-2">
                      <UpdatesEditor
                        content={updateContentHtml}
                        onUpdate={(html) => {
                          setUpdateContentHtml(html);
                          setUpdateText(extractPlainText(html));
                        }}
                        placeholder="Write your announcement with rich formatting..."
                      />
                    </div>
                    <div className="flex gap-2 mt-3">
                      <Button
                        onClick={() => {
                          if (updateText.trim() || updateContentHtml.trim()) {
                            createUpdateMutation.mutate({
                              text: updateText.trim() || extractPlainText(updateContentHtml),
                              contentHtml: updateContentHtml,
                            });
                          }
                        }}
                        disabled={(!updateText.trim() && !updateContentHtml.trim()) || createUpdateMutation.isPending}
                        data-testid="button-post-update"
                      >
                        {createUpdateMutation.isPending ? "Posting..." : "Post Update"}
                      </Button>
                      <Button
                        variant="outline"
                        onClick={() => {
                          setShowUpdateForm(false);
                          setUpdateText("");
                          setUpdateContentHtml("");
                        }}
                        data-testid="button-cancel-update"
                      >
                        Cancel
                      </Button>
                    </div>
                  </div>
                )}

                <div className="space-y-2">
                  {updates.length === 0 ? (
                    <p className="text-sm text-muted-foreground text-center py-4">
                      No updates posted yet
                    </p>
                  ) : (
                    updates.map((update) => (
                      <div
                        key={update.id}
                        className="flex items-start justify-between p-3 border rounded-lg bg-card hover:shadow-sm transition-shadow"
                        data-testid={`update-item-${update.id}`}
                      >
                        <div className="flex-1 mr-2">
                          <p className="text-sm whitespace-pre-wrap">{update.text}</p>
                          <p className="text-xs text-muted-foreground mt-1">
                            Posted {new Date(update.createdAt).toLocaleString()}
                          </p>
                        </div>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            if (confirm("Are you sure you want to delete this update?")) {
                              deleteUpdateMutation.mutate(update.id);
                            }
                          }}
                          className="text-red-600 hover:text-red-700"
                          data-testid={`button-delete-update-${update.id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    ))
                  )}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Suggestions Management Section - Admin Only */}
      {mode === "master" && (isAdmin || isMasterAdmin) && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Lightbulb className="w-5 h-5" />
              Suggestions Management
            </CardTitle>
          </CardHeader>
          <CardContent>
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList className="grid w-full grid-cols-3 md:grid-cols-5">
              <TabsTrigger value="all" data-testid="tab-all">
                All ({statusCounts.all})
              </TabsTrigger>
              <TabsTrigger value="unread" data-testid="tab-unread">
                Unread ({statusCounts.unread})
              </TabsTrigger>
              <TabsTrigger value="done" data-testid="tab-done">
                Done ({statusCounts.done})
              </TabsTrigger>
              <TabsTrigger value="deferred" data-testid="tab-deferred">
                Deferred ({statusCounts.deferred})
              </TabsTrigger>
              <TabsTrigger value="archived" data-testid="tab-archived">
                Archived ({statusCounts.archived})
              </TabsTrigger>
            </TabsList>

            <TabsContent value={activeTab} className="mt-6 space-y-4">
              {suggestionsLoading ? (
                <div className="space-y-4">
                  {[1, 2, 3].map((i) => (
                    <Card key={i}>
                      <CardContent className="pt-6">
                        <Skeleton className="h-4 w-full mb-2" />
                        <Skeleton className="h-4 w-3/4 mb-4" />
                        <Skeleton className="h-4 w-32" />
                      </CardContent>
                    </Card>
                  ))}
                </div>
              ) : filteredSuggestions.length === 0 ? (
                <div className="text-center py-12">
                  <div className="w-16 h-16 bg-muted rounded-full flex items-center justify-center mx-auto mb-4">
                    <Lightbulb className="w-8 h-8 text-muted-foreground" />
                  </div>
                  <h3 className="text-lg font-semibold text-foreground mb-2">
                    No suggestions in this category
                  </h3>
                  <p className="text-muted-foreground">
                    {activeTab === "all"
                      ? "No suggestions have been submitted yet."
                      : `No suggestions with status "${activeTab}".`}
                  </p>
                </div>
              ) : (
                filteredSuggestions.map((suggestion) => (
                  <Card key={suggestion.id} data-testid={`card-suggestion-${suggestion.id}`}>
                    <CardContent className="pt-6">
                      <div className="space-y-4">
                        <div>
                          <p className="text-foreground whitespace-pre-wrap" data-testid={`text-content-${suggestion.id}`}>
                            {suggestion.plainContent}
                          </p>
                        </div>

                        <div className="flex flex-wrap items-center gap-4 text-sm text-muted-foreground">
                          <div className="flex items-center gap-2">
                            <span className="font-medium">User:</span>
                            <span data-testid={`text-user-${suggestion.id}`}>
                              {suggestion.userName} ({suggestion.userEmail})
                            </span>
                          </div>
                          <span>•</span>
                          <div className="flex items-center gap-2">
                            <span className="font-medium">Tenant:</span>
                            <span data-testid={`text-tenant-${suggestion.id}`}>
                              {suggestion.tenantName}
                            </span>
                          </div>
                          <span>•</span>
                          <div className="flex items-center gap-2">
                            <span className="font-medium">Submitted:</span>
                            <span data-testid={`text-date-${suggestion.id}`}>
                              {format(new Date(suggestion.createdAt), "MMM d, yyyy 'at' h:mm a")}
                            </span>
                          </div>
                        </div>

                        <div className="flex items-center gap-2">
                          <span className="text-sm font-medium text-foreground">Status:</span>
                          <Select
                            value={suggestion.status}
                            onValueChange={(value) =>
                              updateStatusMutation.mutate({ id: suggestion.id, status: value })
                            }
                          >
                            <SelectTrigger className="w-[180px]" data-testid={`select-status-${suggestion.id}`}>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="unread">Unread</SelectItem>
                              <SelectItem value="done">Done</SelectItem>
                              <SelectItem value="deferred">Deferred</SelectItem>
                              <SelectItem value="archived">Archived</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))
              )}
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>
      )}

      {/* Tour Management Section - Admin Only */}
      {mode === "master" && (isAdmin || isMasterAdmin) && (
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="flex items-center gap-2">
                  <Sparkles className="w-5 h-5" />
                  Tour Management
                </CardTitle>
                <CardDescription>
                  Create and manage guided tours to help users learn the application
                </CardDescription>
              </div>
              <Link href="/admin/tours">
                <Button data-testid="button-manage-tours">
                  <Layout className="w-4 h-4 mr-2" />
                  Manage Tours
                </Button>
              </Link>
            </div>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <p className="text-sm text-muted-foreground">
                Tours provide interactive step-by-step guidance for users. Create tours to introduce features, 
                explain workflows, or provide contextual help throughout the application.
              </p>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 pt-2">
                <div className="flex flex-col items-center text-center p-4 bg-muted/50 rounded-lg">
                  <Sparkles className="w-8 h-8 text-primary mb-2" />
                  <h4 className="font-medium mb-1">Create Tours</h4>
                  <p className="text-xs text-muted-foreground">
                    Build interactive guides with steps, videos, and spotlights
                  </p>
                </div>
                <div className="flex flex-col items-center text-center p-4 bg-muted/50 rounded-lg">
                  <Layout className="w-8 h-8 text-primary mb-2" />
                  <h4 className="font-medium mb-1">Organize Content</h4>
                  <p className="text-xs text-muted-foreground">
                    Categorize tours by difficulty and topic areas
                  </p>
                </div>
                <div className="flex flex-col items-center text-center p-4 bg-muted/50 rounded-lg">
                  <Users className="w-8 h-8 text-primary mb-2" />
                  <h4 className="font-medium mb-1">Track Progress</h4>
                  <p className="text-xs text-muted-foreground">
                    Monitor user engagement and completion rates
                  </p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {mode === "settings" && (<>
      {/* Theme Builder Section */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <Palette className="w-5 h-5" />
                Theme Builder
              </CardTitle>
              <CardDescription>
                Customize colors and fonts to personalize your experience
              </CardDescription>
            </div>
            <Link href="/admin/theme">
              <Button data-testid="button-theme-builder">
                <Palette className="w-4 h-4 mr-2" />
                Customize Theme
              </Button>
            </Link>
          </div>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">
              Create your perfect workspace with custom colors and fonts. Choose from preset themes or 
              design your own by adjusting individual color variables and typography settings.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 pt-2">
              <div className="flex flex-col items-center text-center p-4 bg-muted/50 rounded-lg">
                <Palette className="w-8 h-8 text-primary mb-2" />
                <h4 className="font-medium mb-1">Custom Colors</h4>
                <p className="text-xs text-muted-foreground">
                  Personalize backgrounds, buttons, and accent colors
                </p>
              </div>
              <div className="flex flex-col items-center text-center p-4 bg-muted/50 rounded-lg">
                <Type className="w-8 h-8 text-primary mb-2" />
                <h4 className="font-medium mb-1">Typography</h4>
                <p className="text-xs text-muted-foreground">
                  Choose fonts that match your style and preferences
                </p>
              </div>
              <div className="flex flex-col items-center text-center p-4 bg-muted/50 rounded-lg">
                <RotateCcw className="w-8 h-8 text-primary mb-2" />
                <h4 className="font-medium mb-1">Light & Dark Modes</h4>
                <p className="text-xs text-muted-foreground">
                  Customize themes separately for each mode
                </p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* User Backup & Restore Section */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Database className="w-5 h-5" />
            User Backup and Restore
          </CardTitle>
          <CardDescription>
            Download your personal data as a backup or restore from a previous backup. This includes only your goals, tasks, habits, and other personal records.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-6">
            {/* Download Backup Section */}
            <div className="p-4 border rounded-lg bg-muted/50">
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-2">
                    <Download className="w-5 h-5 text-primary" />
                    <h3 className="font-semibold">Download Backup</h3>
                  </div>
                  <p className="text-sm text-muted-foreground mb-4">
                    Download all your data including goals, tasks, habits, journal entries, notes, 
                    calendar events, media files, and all settings as a single zip file.
                  </p>
                  <ul className="text-sm text-muted-foreground space-y-1 mb-4">
                    <li className="flex items-center gap-2">
                      <span className="text-primary">✓</span> Goals, milestones, tasks & sub-tasks
                    </li>
                    <li className="flex items-center gap-2">
                      <span className="text-primary">✓</span> Habits & habit entries
                    </li>
                    <li className="flex items-center gap-2">
                      <span className="text-primary">✓</span> Journal entries & templates
                    </li>
                    <li className="flex items-center gap-2">
                      <span className="text-primary">✓</span> Notes, checklists & calendar events
                    </li>
                    <li className="flex items-center gap-2">
                      <span className="text-primary">✓</span> Media files, categories & all settings
                    </li>
                  </ul>
                </div>
                <Button
                  onClick={handleDownloadBackup}
                  disabled={isDownloadingBackup}
                  size="lg"
                  data-testid="button-download-backup"
                >
                  {isDownloadingBackup ? (
                    <>
                      <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                      Downloading...
                    </>
                  ) : (
                    <>
                      <Download className="w-4 h-4 mr-2" />
                      Download Backup
                    </>
                  )}
                </Button>
              </div>
            </div>

            {/* Automated Backup Section */}
            <div className="p-4 border rounded-lg border-blue-500/50 bg-blue-500/5">
              <div className="space-y-4">
                <div className="flex items-start gap-3">
                  <Cloud className="w-5 h-5 text-blue-600 dark:text-blue-400 mt-0.5" />
                  <div className="flex-1">
                    <h3 className="font-semibold text-blue-900 dark:text-blue-100 mb-2">
                      Automated Backup to Google Drive
                    </h3>
                    <p className="text-sm text-blue-800 dark:text-blue-200 mb-3">
                      Automatically backup your data to Google Drive on a scheduled basis. Backups are saved in a dedicated "Backups" folder.
                    </p>
                    
                    {userPreferences?.automatedBackupEnabled ? (
                      <div className="space-y-3">
                        <div className="bg-green-100 dark:bg-green-900/30 border border-green-300 dark:border-green-700 rounded-lg p-3">
                          <div className="flex items-center gap-2 mb-2">
                            <Calendar className="w-4 h-4 text-green-700 dark:text-green-300" />
                            <p className="text-sm font-medium text-green-900 dark:text-green-100">
                              Automated Backup: Enabled
                            </p>
                          </div>
                          <div className="text-sm text-green-800 dark:text-green-200 space-y-1">
                            <p>Frequency: {userPreferences.automatedBackupFrequency?.charAt(0).toUpperCase() + userPreferences.automatedBackupFrequency?.slice(1)}</p>
                            {userPreferences.automatedBackupLastRun && (
                              <p>Last backup: {format(new Date(userPreferences.automatedBackupLastRun), 'PPp')}</p>
                            )}
                          </div>
                        </div>
                        
                        <div className="flex flex-wrap gap-2">
                          <Button
                            onClick={() => runBackupNowMutation.mutate()}
                            disabled={runBackupNowMutation.isPending}
                            variant="default"
                            data-testid="button-run-backup-now"
                          >
                            {runBackupNowMutation.isPending ? (
                              <>
                                <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                                Running Backup...
                              </>
                            ) : (
                              <>
                                <Cloud className="w-4 h-4 mr-2" />
                                Run Backup Now
                              </>
                            )}
                          </Button>

                          <Button
                            onClick={() => testBackupConnectionMutation.mutate()}
                            disabled={testBackupConnectionMutation.isPending}
                            variant="outline"
                            data-testid="button-test-backup-connection"
                          >
                            {testBackupConnectionMutation.isPending ? (
                              <>
                                <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                                Testing...
                              </>
                            ) : (
                              <>
                                <Stethoscope className="w-4 h-4 mr-2" />
                                Test Connection
                              </>
                            )}
                          </Button>
                          
                          <Button
                            onClick={handleDisableAutomatedBackup}
                            disabled={disableAutomatedBackupMutation.isPending}
                            variant="outline"
                            data-testid="button-disable-automated-backup"
                          >
                            {disableAutomatedBackupMutation.isPending ? (
                              <>
                                <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                                Disabling...
                              </>
                            ) : (
                              <>
                                <X className="w-4 h-4 mr-2" />
                                Disable Automated Backup
                              </>
                            )}
                          </Button>
                        </div>
                      </div>
                    ) : (
                      <Button
                        onClick={handleSetupAutomatedBackup}
                        variant="default"
                        data-testid="button-setup-automated-backup"
                      >
                        <Cloud className="w-4 h-4 mr-2" />
                        Setup Automated Backup
                      </Button>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Restore Backup Section */}
            <div className="p-4 border rounded-lg border-amber-500/50 bg-amber-500/5">
              <div className="space-y-4">
                <div className="flex items-start gap-3">
                  <Upload className="w-5 h-5 text-amber-600 dark:text-amber-400 mt-0.5" />
                  <div className="flex-1">
                    <h3 className="font-semibold text-amber-900 dark:text-amber-100 mb-2">
                      Restore from Backup
                    </h3>
                    <div className="bg-amber-100 dark:bg-amber-900/30 border border-amber-300 dark:border-amber-700 rounded-lg p-3 mb-4">
                      <p className="text-sm font-medium text-amber-900 dark:text-amber-100 mb-1">
                        ⚠️ Warning: This action is destructive and cannot be undone
                      </p>
                      <p className="text-sm text-amber-800 dark:text-amber-200">
                        Restoring a backup will permanently delete all your current data 
                        and replace it with the data from the backup file.
                      </p>
                    </div>
                    
                    <div className="space-y-3">
                      <Label htmlFor="backup-file" className="text-sm font-medium">
                        Select backup file (.zip)
                      </Label>
                      <Input
                        id="backup-file"
                        type="file"
                        accept=".zip"
                        onChange={(e) => setBackupFile(e.target.files?.[0] || null)}
                        disabled={restoreBackupMutation.isPending}
                        data-testid="input-backup-file"
                        className="cursor-pointer"
                      />
                      
                      {backupFile && (
                        <div className="flex items-center gap-2 text-sm text-muted-foreground">
                          <Database className="w-4 h-4" />
                          <span>{backupFile.name}</span>
                          <span className="text-xs">
                            ({(backupFile.size / 1024 / 1024).toFixed(2)} MB)
                          </span>
                        </div>
                      )}

                      <Button
                        onClick={handleRestoreBackup}
                        disabled={!backupFile || restoreBackupMutation.isPending}
                        variant="destructive"
                        size="lg"
                        data-testid="button-restore-backup"
                      >
                        {restoreBackupMutation.isPending ? (
                          <>
                            <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                            Restoring...
                          </>
                        ) : (
                          <>
                            <Upload className="w-4 h-4 mr-2" />
                            Restore Backup
                          </>
                        )}
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
      </>)}

      {/* Production Database Backup Section (Master Admin Only) */}
      {mode === "master" && isMasterAdmin && (
        <Card id="production-backup-section" className="border-red-500/50">
          <CardHeader className="bg-red-500/5">
            <CardTitle className="flex items-center gap-2 text-red-700 dark:text-red-400">
              <Database className="w-5 h-5" />
              Production Database Backup
              <span className="text-xs font-normal bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300 px-2 py-1 rounded-full border border-red-300 dark:border-red-700">
                Master Admin Only
              </span>
            </CardTitle>
            <CardDescription className="text-red-900/70 dark:text-red-100/70">
              Backup or restore the ENTIRE production database including ALL users and ALL tenants. Use with extreme caution.
            </CardDescription>
          </CardHeader>
          <CardContent className="pt-6">
            <div className="space-y-6">
              {/* Production Download Backup Section */}
              <div className="p-4 border rounded-lg border-blue-500/50 bg-blue-500/5">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <Download className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                      <h3 className="font-semibold text-blue-900 dark:text-blue-100">Download Production Backup</h3>
                    </div>
                    <p className="text-sm text-blue-800 dark:text-blue-200 mb-3">
                      Creates a complete backup of the entire production database using pg_dump. 
                      This backup includes all tenants, all users, and all data.
                    </p>
                    <ul className="text-sm text-blue-700 dark:text-blue-300 space-y-1 mb-3">
                      <li className="flex items-center gap-2">
                        <span className="text-blue-500">✓</span> Full database schema and data
                      </li>
                      <li className="flex items-center gap-2">
                        <span className="text-blue-500">✓</span> All tenants and users
                      </li>
                      <li className="flex items-center gap-2">
                        <span className="text-blue-500">✓</span> All relationships and constraints
                      </li>
                      <li className="flex items-center gap-2">
                        <span className="text-blue-500">✓</span> Includes manifest with metadata
                      </li>
                    </ul>
                    <p className="text-xs text-blue-600 dark:text-blue-400 bg-blue-100 dark:bg-blue-900/30 border border-blue-300 dark:border-blue-700 rounded p-2">
                      ℹ️ Rate limited to 5 downloads per hour for security
                    </p>
                  </div>
                  <Button
                    onClick={handleDownloadProductionBackup}
                    disabled={isDownloadingProductionBackup}
                    size="lg"
                    data-testid="button-download-production-backup"
                    className="bg-blue-600 hover:bg-blue-700"
                  >
                    {isDownloadingProductionBackup ? (
                      <>
                        <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                        Downloading...
                      </>
                    ) : (
                      <>
                        <Download className="w-4 h-4 mr-2" />
                        Download Production Backup
                      </>
                    )}
                  </Button>
                </div>
              </div>

              {/* Production Restore Backup Section */}
              <div className="p-4 border-2 rounded-lg border-red-500 bg-red-500/10">
                <div className="space-y-4">
                  <div className="flex items-start gap-3">
                    <Upload className="w-5 h-5 text-red-600 dark:text-red-400 mt-0.5" />
                    <div className="flex-1">
                      <h3 className="font-semibold text-red-900 dark:text-red-100 mb-2">
                        Restore Production Database
                      </h3>
                      
                      <div className="bg-red-100 dark:bg-red-900/40 border-2 border-red-500 rounded-lg p-4 mb-4">
                        <p className="text-sm font-bold text-red-900 dark:text-red-100 mb-2 flex items-center gap-2">
                          🚨 CRITICAL WARNING - READ CAREFULLY
                        </p>
                        <ul className="text-sm text-red-800 dark:text-red-200 space-y-2">
                          <li className="flex items-start gap-2">
                            <span className="text-red-600 font-bold">•</span>
                            <span>This will PERMANENTLY DELETE the ENTIRE production database</span>
                          </li>
                          <li className="flex items-start gap-2">
                            <span className="text-red-600 font-bold">•</span>
                            <span>ALL USERS from ALL TENANTS will lose their current data</span>
                          </li>
                          <li className="flex items-start gap-2">
                            <span className="text-red-600 font-bold">•</span>
                            <span>This action is IRREVERSIBLE and cannot be undone</span>
                          </li>
                          <li className="flex items-start gap-2">
                            <span className="text-red-600 font-bold">•</span>
                            <span>The entire system will be affected immediately</span>
                          </li>
                          <li className="flex items-start gap-2">
                            <span className="text-red-600 font-bold">•</span>
                            <span>Rate limited to 5 restore attempts per hour</span>
                          </li>
                        </ul>
                        <p className="text-xs font-bold text-red-900 dark:text-red-100 mt-3 pt-3 border-t border-red-400 dark:border-red-600">
                          ⚠️ Only use this if you are absolutely certain you need to restore from a backup
                        </p>
                      </div>
                      
                      <div className="space-y-3">
                        <Label htmlFor="production-backup-file" className="text-sm font-medium text-red-900 dark:text-red-100">
                          Select production backup file (.zip)
                        </Label>
                        <Input
                          id="production-backup-file"
                          type="file"
                          accept=".zip"
                          onChange={(e) => setProductionBackupFile(e.target.files?.[0] || null)}
                          disabled={restoreProductionBackupMutation.isPending}
                          data-testid="input-production-backup-file"
                          className="cursor-pointer border-red-300 dark:border-red-700"
                        />
                        
                        {productionBackupFile && (
                          <div className="flex items-center gap-2 text-sm text-muted-foreground bg-red-50 dark:bg-red-900/20 p-2 rounded border border-red-200 dark:border-red-800">
                            <Database className="w-4 h-4 text-red-600" />
                            <span className="font-medium">{productionBackupFile.name}</span>
                            <span className="text-xs">
                              ({(productionBackupFile.size / 1024 / 1024).toFixed(2)} MB)
                            </span>
                          </div>
                        )}

                        <Button
                          onClick={handleRestoreProductionBackup}
                          disabled={!productionBackupFile || restoreProductionBackupMutation.isPending}
                          variant="destructive"
                          size="lg"
                          data-testid="button-restore-production-backup"
                          className="bg-red-600 hover:bg-red-700 w-full"
                        >
                          {restoreProductionBackupMutation.isPending ? (
                            <>
                              <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                              Restoring Production Database...
                            </>
                          ) : (
                            <>
                              <Upload className="w-4 h-4 mr-2" />
                              Restore Production Database
                            </>
                          )}
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* JSON Restore Section */}
              <div className="p-4 border-2 rounded-lg border-amber-500 bg-amber-500/10">
                <div className="space-y-4">
                  <div className="flex items-start gap-3">
                    <FileJson className="w-5 h-5 text-amber-600 dark:text-amber-400 mt-0.5" />
                    <div className="flex-1">
                      <h3 className="font-semibold text-amber-900 dark:text-amber-100 mb-2">
                        Restore from JSON Backup
                      </h3>
                      
                      <div className="bg-amber-100 dark:bg-amber-900/40 border border-amber-400 dark:border-amber-600 rounded-lg p-4 mb-4">
                        <p className="text-sm text-amber-800 dark:text-amber-200 mb-2">
                          JSON restore provides more flexibility than SQL restore:
                        </p>
                        <ul className="text-sm text-amber-700 dark:text-amber-300 space-y-1">
                          <li className="flex items-center gap-2">
                            <span className="text-amber-600">✓</span> Preview changes with Dry Run before restoring
                          </li>
                          <li className="flex items-center gap-2">
                            <span className="text-amber-600">✓</span> Choose between Full Replace or Merge mode
                          </li>
                          <li className="flex items-center gap-2">
                            <span className="text-amber-600">✓</span> Cross-platform compatible JSON format
                          </li>
                        </ul>
                      </div>
                      
                      <div className="space-y-3">
                        <Label htmlFor="json-backup-file" className="text-sm font-medium text-amber-900 dark:text-amber-100">
                          Select JSON backup file (.json)
                        </Label>
                        <Input
                          id="json-backup-file"
                          type="file"
                          accept=".json"
                          onChange={(e) => {
                            setJsonBackupFile(e.target.files?.[0] || null);
                            setJsonDryRunResult(null);
                          }}
                          disabled={restoreJsonBackupMutation.isPending}
                          data-testid="input-json-backup-file"
                          className="cursor-pointer border-amber-300 dark:border-amber-700"
                        />
                        
                        {jsonBackupFile && (
                          <div className="flex items-center gap-2 text-sm text-muted-foreground bg-amber-50 dark:bg-amber-900/20 p-2 rounded border border-amber-200 dark:border-amber-800">
                            <FileJson className="w-4 h-4 text-amber-600" />
                            <span className="font-medium">{jsonBackupFile.name}</span>
                            <span className="text-xs">
                              ({(jsonBackupFile.size / 1024 / 1024).toFixed(2)} MB)
                            </span>
                          </div>
                        )}

                        <div className="space-y-2">
                          <Label className="text-sm font-medium text-amber-900 dark:text-amber-100">
                            Restore Mode
                          </Label>
                          <Select
                            value={jsonRestoreMode}
                            onValueChange={(value: 'full-replace' | 'merge') => setJsonRestoreMode(value)}
                          >
                            <SelectTrigger data-testid="select-json-restore-mode" className="border-amber-300 dark:border-amber-700">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="full-replace">Full Replace - Delete all existing data first</SelectItem>
                              <SelectItem value="merge">Merge - Add to existing data (skip conflicts)</SelectItem>
                            </SelectContent>
                          </Select>
                          <p className="text-xs text-amber-600 dark:text-amber-400">
                            {jsonRestoreMode === 'full-replace' 
                              ? '⚠️ Full Replace will DELETE ALL EXISTING DATA before restoring'
                              : 'ℹ️ Merge will add new records and skip existing ones (by ID)'}
                          </p>
                        </div>

                        {jsonDryRunResult && (
                          <div className="bg-green-50 dark:bg-green-900/20 border border-green-300 dark:border-green-700 rounded-lg p-3">
                            <p className="text-sm font-medium text-green-800 dark:text-green-200 mb-2">
                              ✅ Dry Run Preview ({jsonDryRunResult.tablesRestored.length} tables)
                            </p>
                            <div className="max-h-32 overflow-y-auto">
                              <div className="text-xs text-green-700 dark:text-green-300 grid grid-cols-2 gap-1">
                                {Object.entries(jsonDryRunResult.recordCounts).slice(0, 20).map(([table, count]) => (
                                  <span key={table}>{table}: {count}</span>
                                ))}
                                {Object.keys(jsonDryRunResult.recordCounts).length > 20 && (
                                  <span>...and {Object.keys(jsonDryRunResult.recordCounts).length - 20} more tables</span>
                                )}
                              </div>
                            </div>
                            <p className="text-xs text-green-600 dark:text-green-400 mt-2">
                              Total: {Object.values(jsonDryRunResult.recordCounts).reduce((a, b) => a + b, 0)} records
                            </p>
                          </div>
                        )}

                        <div className="flex gap-2">
                          <Button
                            onClick={handleJsonDryRun}
                            disabled={!jsonBackupFile || restoreJsonBackupMutation.isPending}
                            variant="outline"
                            data-testid="button-json-dry-run"
                            className="flex-1 border-amber-400 text-amber-700 hover:bg-amber-50 dark:border-amber-600 dark:text-amber-300 dark:hover:bg-amber-900/20"
                          >
                            {restoreJsonBackupMutation.isPending && !jsonDryRunResult ? (
                              <>
                                <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                                Analyzing...
                              </>
                            ) : (
                              <>
                                <Search className="w-4 h-4 mr-2" />
                                Preview (Dry Run)
                              </>
                            )}
                          </Button>

                          <Button
                            onClick={handleRestoreJsonBackup}
                            disabled={!jsonBackupFile || restoreJsonBackupMutation.isPending}
                            variant="destructive"
                            data-testid="button-restore-json-backup"
                            className="flex-1 bg-amber-600 hover:bg-amber-700"
                          >
                            {restoreJsonBackupMutation.isPending && jsonDryRunResult ? (
                              <>
                                <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                                Restoring...
                              </>
                            ) : (
                              <>
                                <Upload className="w-4 h-4 mr-2" />
                                Restore from JSON
                              </>
                            )}
                          </Button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Automated Backup Setup Dialog */}
      <Dialog open={showAutomatedBackupDialog} onOpenChange={setShowAutomatedBackupDialog}>
        <DialogContent className="w-screen h-screen max-w-none max-h-none m-0 rounded-none overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Cloud className="w-5 h-5" />
              Setup Automated Backup
            </DialogTitle>
            <DialogDescription>
              Choose how often you want to automatically backup your data to Google Drive. Backups will be saved to your Drive/GoalVia Files/Backups folder.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="frequency">Backup Frequency</Label>
              <Select
                value={automatedBackupFrequency}
                onValueChange={(value: 'daily' | 'weekly' | 'monthly') => setAutomatedBackupFrequency(value)}
              >
                <SelectTrigger id="frequency" data-testid="select-backup-frequency">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="daily">Daily - Every 24 hours</SelectItem>
                  <SelectItem value="weekly">Weekly - Every 7 days</SelectItem>
                  <SelectItem value="monthly">Monthly - Every 30 days</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-3">
              <p className="text-sm text-blue-900 dark:text-blue-100">
                <strong>ℹ️ What happens:</strong>
              </p>
              <ul className="text-sm text-blue-800 dark:text-blue-200 mt-2 space-y-1 ml-4">
                <li>• A "Backups" folder will be created in your Google Drive</li>
                <li>• Backups run automatically based on your chosen frequency</li>
                <li>• Each backup is saved as a dated zip file</li>
                <li>• You can disable automated backups anytime</li>
              </ul>
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowAutomatedBackupDialog(false)}
              data-testid="button-cancel-automated-backup"
            >
              Cancel
            </Button>
            <Button
              onClick={handleConfirmAutomatedBackup}
              disabled={setupAutomatedBackupMutation.isPending}
              data-testid="button-confirm-automated-backup"
            >
              {setupAutomatedBackupMutation.isPending ? (
                <>
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                  Setting up...
                </>
              ) : (
                <>
                  <Cloud className="w-4 h-4 mr-2" />
                  Enable Automated Backup
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Backup Connection Test Results Dialog */}

      {/* ─── AI Usage Section ─── */}
      {mode === "master" && isMasterAdmin && (
        <SectionErrorBoundary title="AI Usage Dashboard">
          <AiUsageSection />
        </SectionErrorBoundary>
      )}

      {/* ─── Mobile App Features Section ─── */}
      {mode === "master" && isMasterAdmin && (
        <SectionErrorBoundary title="Mobile App Features">
          <MobileAppSection />
        </SectionErrorBoundary>
      )}

      {/* ─── Audit Logs Section ─── */}
      {mode === "master" && isMasterAdmin && (
        <SectionErrorBoundary title="Audit Log">
          <AuditLogSection />
        </SectionErrorBoundary>
      )}

      {/* ─── Dunning Config Section ─── */}
      {mode === "master" && isMasterAdmin && (
        <SectionErrorBoundary title="Dunning Workflow">
          <DunningConfigSection />
        </SectionErrorBoundary>
      )}

      {/* ─── Reclaim Legacy Agent Tasks Section ─── */}
      {mode === "master" && isMasterAdmin && (
        <SectionErrorBoundary title="Reclaim Legacy Agent Tasks">
          <ManusReclaimSection />
        </SectionErrorBoundary>
      )}

      {/* ─── CSP Violations Section ─── */}
      {mode === "master" && isMasterAdmin && (
        <SectionErrorBoundary title="CSP Violations">
          <CspViolationsSection />
        </SectionErrorBoundary>
      )}

      <Dialog open={showBackupTestDialog} onOpenChange={setShowBackupTestDialog}>
        <DialogContent className="w-screen h-screen max-w-none max-h-none m-0 rounded-none overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Stethoscope className="w-5 h-5" />
              Backup Connection Test Results
            </DialogTitle>
            <DialogDescription>
              Diagnostic results for your Google Drive backup connection
            </DialogDescription>
          </DialogHeader>

          {backupTestResults && (
            <div className="space-y-4">
              <div className={`p-3 rounded-lg flex items-center gap-2 ${
                backupTestResults.success 
                  ? 'bg-green-100 dark:bg-green-900/30 text-green-900 dark:text-green-100' 
                  : 'bg-red-100 dark:bg-red-900/30 text-red-900 dark:text-red-100'
              }`}>
                {backupTestResults.success ? (
                  <CheckCircle2 className="w-5 h-5" />
                ) : (
                  <XCircle className="w-5 h-5" />
                )}
                <span className="font-medium">
                  {backupTestResults.success 
                    ? 'All checks passed - Drive backup should work' 
                    : 'Some checks failed - see details below'}
                </span>
              </div>

              {backupTestResults.diagnostics?.steps?.map((step: any, index: number) => (
                <div 
                  key={index} 
                  className={`p-3 rounded-lg border ${
                    step.status === 'passed' 
                      ? 'border-green-300 dark:border-green-700 bg-green-50 dark:bg-green-900/20' 
                      : step.status === 'failed' 
                        ? 'border-red-300 dark:border-red-700 bg-red-50 dark:bg-red-900/20'
                        : 'border-gray-300 dark:border-gray-700'
                  }`}
                >
                  <div className="flex items-center gap-2 mb-2">
                    {step.status === 'passed' ? (
                      <CheckCircle2 className="w-4 h-4 text-green-600 dark:text-green-400" />
                    ) : step.status === 'failed' ? (
                      <XCircle className="w-4 h-4 text-red-600 dark:text-red-400" />
                    ) : (
                      <RefreshCw className="w-4 h-4 text-gray-500" />
                    )}
                    <span className="font-medium text-sm">
                      Step {step.step}: {step.name}
                    </span>
                  </div>
                  
                  {step.details && (
                    <div className="text-xs text-muted-foreground bg-muted/50 rounded p-2 font-mono overflow-x-auto">
                      {Object.entries(step.details).map(([key, value]) => (
                        <div key={key}>
                          <span className="text-blue-600 dark:text-blue-400">{key}:</span>{' '}
                          {typeof value === 'boolean' ? (value ? 'Yes' : 'No') : String(value)}
                        </div>
                      ))}
                    </div>
                  )}
                  
                  {step.error && (
                    <div className="mt-2 text-sm text-red-700 dark:text-red-300">
                      <strong>Error:</strong> {step.error}
                    </div>
                  )}
                  
                  {step.solution && (
                    <div className="mt-2 text-sm text-amber-700 dark:text-amber-300">
                      <strong>Solution:</strong> {step.solution}
                    </div>
                  )}
                </div>
              ))}

              <div className="text-xs text-muted-foreground">
                Test run at: {backupTestResults.diagnostics?.timestamp}
              </div>
            </div>
          )}

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowBackupTestDialog(false)}
              data-testid="button-close-backup-test"
            >
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// Export with error boundary wrapper to prevent blank pages on unexpected errors
export function UserSettingsPage() {
  return (
    <AdminPanelErrorBoundary>
      <AdminPanelPageContent mode="settings" />
    </AdminPanelErrorBoundary>
  );
}

export function MasterAdminPage() {
  return (
    <AdminPanelErrorBoundary>
      <AdminPanelPageContent mode="master" />
    </AdminPanelErrorBoundary>
  );
}

// Legacy route (/admin/panel) now shows the user-level Settings page
export default function AdminPanelPage() {
  return <UserSettingsPage />;
}
